/*
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║          GODDESS SELENE — OFFICIAL WEBSITE v5.4                ║
 * ║          Last Updated: April 8, 2026                           ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * ═══════════════════════════════════════════════════════════════════
 *  HOSTING & DEPLOYMENT INSTRUCTIONS
 * ═══════════════════════════════════════════════════════════════════
 *
 *  OPTION 1: VERCEL (Recommended — Free & Easy)
 *  ─────────────────────────────────────────────
 *  1. Create an account at https://vercel.com
 *  2. Install Node.js from https://nodejs.org (LTS version)
 *  3. Open your terminal/command prompt and run:
 *       npx create-react-app goddess-selene
 *       cd goddess-selene
 *  4. Replace the contents of src/App.js with THIS ENTIRE FILE
 *  5. In src/App.js, change the FIRST line to:
 *       import React, { useState, useEffect, useRef, useCallback } from "react";
 *     (Add "React," at the start of the import)
 *  6. Run: npm start  (to test locally at http://localhost:3000)
 *  7. Push to GitHub, then connect that repo to Vercel
 *  8. Vercel auto-deploys on every push — edit code, push, site updates!
 *
 *  OPTION 2: NETLIFY (Also Free)
 *  ─────────────────────────────
 *  1. Same steps 1-6 as above
 *  2. Run: npm run build
 *  3. Drag the "build" folder into https://app.netlify.com/drop
 *  4. To update: re-run build, re-drag, done!
 *
 *  OPTION 3: CUSTOM DOMAIN
 *  ───────────────────────
 *  1. Buy a domain (Namecheap, GoDaddy, Google Domains)
 *  2. In Vercel/Netlify dashboard → Settings → Domains → Add your domain
 *  3. Update your domain's DNS nameservers to point to Vercel/Netlify
 *  4. SSL/HTTPS is automatic
 *
 *  HOW TO EDIT & UPDATE LIVE:
 *  ──────────────────────────
 *  1. Search for 🔧 EDIT: tags below to find each editable section
 *  2. Make your changes and save
 *  3. Push to GitHub → Vercel auto-deploys in ~30 seconds
 *
 *  HOW TO ADD EXTERNAL LINKS:
 *  ──────────────────────────
 *  Wrap any button/text in:
 *    <a href="https://YOUR-URL" target="_blank" rel="noopener noreferrer">
 *      <button className="btn-primary">Button Text</button>
 *    </a>
 *
 *  QUICK SEARCH TAGS:
 *    🔧 EDIT: EXPENSES        → Add/remove expense items + images
 *    🔧 EDIT: LEADERBOARD     → Update devotee rankings
 *    🔧 EDIT: WHEEL OPTIONS   → Change spin wheel prizes (25 options)
 *    🔧 EDIT: TASKS           → Add/remove sub tasks (25 tasks)
 *    🔧 EDIT: FREE CONTENT    → Add/remove free content items
 *    🔧 EDIT: SOCIAL LINKS    → Update social media URLs
 *    🔧 EDIT: LINK PAGE       → Update the dedicated links page
 *    🔧 EDIT: THRONE          → Throne URL (LOCKED)
 *    🔧 EDIT: TAGLINE         → Hero section tagline text
 *    🔧 EDIT: NAV ITEMS       → Navigation menu items
 *
 * ═══════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ClerkProvider, SignedIn, SignedOut, SignIn, SignUp, useUser, useClerk } from "@clerk/clerk-react";
import { createClient } from "@supabase/supabase-js";

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: SUPABASE CONFIG
 * ═══════════════════════════════════════════════════════════════════
 * SETUP:
 *  1. Go to https://supabase.com and create a free account
 *  2. Create a new project → wait for it to initialize (~2 min)
 *  3. Go to Settings → API
 *  4. Copy "Project URL" and "anon public" key
 *  5. Paste them below (or set as environment variables in Vercel)
 *  6. Run the supabase-schema.sql file in the SQL Editor
 *  7. Install: npm install @supabase/supabase-js
 * ═══════════════════════════════════════════════════════════════════ */
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://wiyredmsyppybxnixxdx.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "sb_publishable_PP-uMjRyEiDKHmL1mKxXUQ_q-IhGtU_";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: CLERK KEY
 * ═══════════════════════════════════════════════════════════════════
 * SETUP INSTRUCTIONS:
 *  1. Go to https://clerk.com and create a free account
 *  2. Create a new application → name it "Goddess Selene"
 *  3. Choose sign-in methods (Email, Google, Twitter — pick what you want)
 *  4. Go to API Keys → copy your "Publishable Key" (starts with pk_)
 *  5. OPTION A: Paste it directly below (replace the placeholder)
 *  6. OPTION B (Better): Add it as an environment variable in Vercel:
 *     → Vercel → Project → Settings → Environment Variables
 *     → Name: REACT_APP_CLERK_PUBLISHABLE_KEY
 *     → Value: pk_live_xxxxx (your actual key)
 *  7. Install Clerk: run "npm install @clerk/clerk-react" in your project
 *  8. Push to GitHub → auto-deploys
 * ═══════════════════════════════════════════════════════════════════ */
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "pk_test_ZGVmaW5pdGUtZmxvdW5kZXItNjcuY2xlcmsuYWNjb3VudHMuZGV2JA";

/* ═══════════════════════════════════════════════════════════════════
 * User Profile Dropdown — shows in top-right corner when logged in
 * ═══════════════════════════════════════════════════════════════════ */
function UserProfileDropdown({ onOpenProfile, profile }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (!user) return null;

  const joinDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  }) : "Unknown";

  const lastActive = user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
  }) : "Now";

  const displayName = profile?.nickname || user.username || user.firstName || user.emailAddresses?.[0]?.emailAddress?.split("@")[0] || "Devotee";
  const avatarUrl = (profile?.avatar_url && profile?.avatar_approved) ? profile.avatar_url : null;

  return (
    <div ref={dropdownRef} style={{ position: "fixed", top: 16, right: 16, zIndex: 1001 }}>
      <button onClick={() => setOpen(!open)} style={{
        width: 52, height: 52, borderRadius: "50%", border: "2px solid rgba(220,20,60,.6)",
        background: avatarUrl ? "none" : "#2a0a0a",
        cursor: "pointer", overflow: "hidden", padding: 0,
        boxShadow: "0 2px 12px rgba(220,20,60,.3)", transition: "all .3s"
      }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <svg width="52" height="52" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="#2a0a0a" />
            <circle cx="20" cy="15" r="7" fill="#666" />
            <ellipse cx="20" cy="35" rx="12" ry="8" fill="#666" />
            <line x1="12" y1="18" x2="28" y2="18" stroke="#555" strokeWidth="1.2" />
            <circle cx="20" cy="18" r="2.2" fill="#DC143C" />
          </svg>
        )}
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div style={{
          position: "absolute", top: 60, right: 0, width: 260,
          background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
          border: "1px solid rgba(220,20,60,.3)", borderRadius: 16,
          padding: "20px", boxShadow: "0 12px 40px rgba(0,0,0,.6)",
          animation: "fadeIn .2s ease", backdropFilter: "blur(20px)"
        }}>
          {/* User Avatar + Name */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid rgba(220,20,60,.15)" }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", overflow: "hidden",
              background: "#2a0a0a",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, border: "2px solid rgba(220,20,60,.4)"
            }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <svg width="44" height="44" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="20" fill="#2a0a0a" />
                  <circle cx="20" cy="15" r="7" fill="#666" />
                  <ellipse cx="20" cy="35" rx="12" ry="8" fill="#666" />
                  <line x1="12" y1="18" x2="28" y2="18" stroke="#555" strokeWidth="1.2" />
                  <circle cx="20" cy="18" r="2.2" fill="#DC143C" />
                </svg>
              )}
            </div>
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: "#ff6b8a" }}>
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: "rgba(245,230,232,.4)" }}>Devotee</div>
            </div>
          </div>

          {/* Info Rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)", display: "flex", alignItems: "center", gap: 6 }}>
                📅 Joined
              </span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{joinDate}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)", display: "flex", alignItems: "center", gap: 6 }}>
                🕐 Last Active
              </span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{lastActive}</span>
            </div>
          </div>

          {/* Profile Button */}
          <button onClick={() => { setOpen(false); if(onOpenProfile) onOpenProfile(); }} style={{
            width: "100%", padding: "10px", marginBottom: 8,
            background: "linear-gradient(135deg, #DC143C, #8B0000)",
            border: "none", borderRadius: 10,
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1,
            textTransform: "uppercase", transition: "all .3s",
            boxShadow: "0 2px 12px rgba(220,20,60,.3)",
          }}>
            👤 My Profile
          </button>

          {/* Logout Button */}
          <button onClick={() => signOut()} style={{
            width: "100%", padding: "10px", background: "rgba(220,20,60,.1)",
            border: "1px solid rgba(220,20,60,.3)", borderRadius: 10,
            color: "#DC143C", fontSize: 13, fontWeight: 700, cursor: "pointer",
            fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1,
            textTransform: "uppercase", transition: "all .3s"
          }}>
            🚪 Log Out
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * Sign-In Page — shown to unauthenticated visitors
 * Styled to match the Goddess Selene aesthetic
 * ═══════════════════════════════════════════════════════════════════ */
function AuthGate() {
  const [mode, setMode] = useState("signin");

  return (
    <div style={{
      minHeight: "100vh", background: "#0a0000",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", padding: 20,
      fontFamily: "'Cormorant Garamond', serif"
    }}>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=Playfair+Display:wght@400;700;900&display=swap");
        @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(220,20,60,.3); } 50% { box-shadow: 0 0 40px rgba(220,20,60,.6); } }
        @keyframes crownFloat { 0%,100% { transform: rotate(-3deg) translateY(0); } 50% { transform: rotate(3deg) translateY(-6px); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Background effects */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", top: "-20%", right: "-10%", width: 600, height: 600, background: "radial-gradient(circle, rgba(220,20,60,.08) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "-10%", width: 500, height: 500, background: "radial-gradient(circle, rgba(139,0,0,.06) 0%, transparent 70%)", borderRadius: "50%" }} />
      </div>

      <div style={{ textAlign: "center", marginBottom: 32, position: "relative", zIndex: 1, animation: "fadeIn .6s ease" }}>
        <div style={{ fontSize: 48, marginBottom: 12, animation: "crownFloat 3s ease infinite" }}>👑</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 900, lineHeight: 1.1, marginBottom: 8 }}>
          <span style={{ color: "#f5e6e8" }}>Goddess </span>
          <span style={{ background: "linear-gradient(135deg, #DC143C, #ff6b8a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Selene</span>
        </h1>
        <p style={{ color: "rgba(245,230,232,.5)", fontSize: 16, fontStyle: "italic" }}>
          Sign in to enter the realm of devotion
        </p>
        <p style={{ color: "rgba(245,230,232,.3)", fontSize: 12, marginTop: 8 }}>
          You must be 18+ to create an account
        </p>
      </div>

      <div style={{ position: "relative", zIndex: 1, animation: "fadeIn .6s ease .2s both" }}>
        {mode === "signin" ? (
          <SignIn
            appearance={{
              variables: {
                colorPrimary: "#DC143C",
                colorBackground: "#1a0505",
                colorText: "#f5e6e8",
                colorInputBackground: "#0a0000",
                colorInputText: "#f5e6e8",
                borderRadius: "12px",
              },
              elements: {
                socialButtonsBlockButton: {
                  backgroundColor: "#2a0a0a",
                  borderColor: "rgba(220,20,60,.4)",
                  color: "#f5e6e8",
                  "&:hover": { backgroundColor: "#3a0f0f", borderColor: "#DC143C" },
                },
                socialButtonsBlockButtonText: { color: "#f5e6e8", fontWeight: 600 },
                dividerLine: { backgroundColor: "rgba(220,20,60,.2)" },
                dividerText: { color: "rgba(245,230,232,.4)" },
                footerActionLink: { color: "#ff6b8a" },
              },
            }}
          />
        ) : (
          <SignUp
            appearance={{
              variables: {
                colorPrimary: "#DC143C",
                colorBackground: "#1a0505",
                colorText: "#f5e6e8",
                colorInputBackground: "#0a0000",
                colorInputText: "#f5e6e8",
                borderRadius: "12px",
              },
              elements: {
                socialButtonsBlockButton: {
                  backgroundColor: "#2a0a0a",
                  borderColor: "rgba(220,20,60,.4)",
                  color: "#f5e6e8",
                  "&:hover": { backgroundColor: "#3a0f0f", borderColor: "#DC143C" },
                },
                socialButtonsBlockButtonText: { color: "#f5e6e8", fontWeight: 600 },
                dividerLine: { backgroundColor: "rgba(220,20,60,.2)" },
                dividerText: { color: "rgba(245,230,232,.4)" },
                footerActionLink: { color: "#ff6b8a" },
              },
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * UTILITY: Real-time "time ago" formatting
 * Converts a timestamp (Date.now() value) to human-readable relative time
 * ═══════════════════════════════════════════════════════════════════ */
function timeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 5) return "Just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

/* ═══════════════════════════════════════════════════════════════════
 * LoyalFans Widget Component
 * Loads the LoyalFans widget script and renders the embed
 * ═══════════════════════════════════════════════════════════════════ */
function LoyalFansWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Remove old script if it exists so it re-initializes
    const oldScript = document.getElementById("lf-widget-v3");
    if (oldScript) oldScript.remove();

    // Create fresh script — forces LoyalFans to scan for widget divs again
    const script = document.createElement("script");
    script.id = "lf-widget-v3";
    script.src = "https://www.loyalfans.com/widget/js/widget.v3.js?t=" + Date.now();
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const s = document.getElementById("lf-widget-v3");
      if (s) s.remove();
    };
  }, []);

  return (
    <div ref={containerRef} style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
      <div
        className="lf-widget-v3"
        data-id="selenedommex"
        data-t="red"
        data-width="336"
        data-height="280"
        data-s="size1"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: THRONE — LOCKED to Goddess Selene's page
 * DO NOT change this. Hardcoded for security.
 * ═══════════════════════════════════════════════════════════════════ */
const THRONE_URL = "https://throne.com/selenedommexo";

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: EXPENSES (Reimburse Section)
 * ═══════════════════════════════════════════════════════════════════
 * Two types of items:
 *
 * SINGLE PRICE: { item, amount, icon, image }
 *   → Shows one price with one "Reimburse" button
 *
 * TIERED PRICE: { item, tiers: [100, 250, 500], icon, image }
 *   → Shows multiple price buttons the user can choose from
 *
 * image: null = shows emoji icon. Set to a URL string to show a photo.
 * ICON REFERENCE: 💅 👜 🧖‍♀️ 💇‍♀️ ✈️ 🛍️ 🥂 🍸 💳 💸 🔥 💎 🏨
 * ═══════════════════════════════════════════════════════════════════ */
const EXPENSES = [
  { id: 1, item: "Luxury Spa Day", amount: 400, icon: "🧖‍♀️", image: null },
  { id: 2, item: "Manicure & Pedicure", amount: 250, icon: "💅", image: null },
  { id: 3, item: "Pay My Rent", amount: 1500, icon: "🏠", image: null },
  { id: 4, item: "Help Me Travel", tiers: [100, 250, 500], icon: "✈️", image: null },
  { id: 5, item: "Shopping Spree!", tiers: [100, 250, 500], icon: "🛍️", image: null },
  { id: 6, item: "Pay for a Night Out", tiers: [100, 250, 500], icon: "🍸", image: null },
  { id: 7, item: "Drain Your Wallet", tiers: [50, 100, 250, 500, 1000], icon: "🔥", image: null },
  // ──── ADD NEW EXPENSES BELOW THIS LINE ────
  // SINGLE: { id: 8, item: "Item Name", amount: 100, icon: "💎", image: null },
  // TIERED: { id: 9, item: "Item Name", tiers: [50, 100, 250], icon: "💸", image: null },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: ADOPT A BILL — Recurring bills subs can sponsor
 * ═══════════════════════════════════════════════════════════════════
 * Each bill has: item, icon, image, and options (array of { label, amount })
 *
 * To ADD: copy a block, paste before ], update values
 * To REMOVE: delete the { ... }, block
 * ═══════════════════════════════════════════════════════════════════ */
const ADOPT_BILLS = [
  {
    id: 1,
    item: "Groceries",
    icon: "🛒",
    image: null,
    options: [
      { label: "$150 / week", amount: 150 },
      { label: "$600 / month", amount: 600 },
    ],
  },
  {
    id: 2,
    item: "Medical Bills",
    icon: "🏥",
    image: null,
    options: [
      { label: "$300 / bi-weekly", amount: 300 },
      { label: "$600 / month", amount: 600 },
    ],
  },
  {
    id: 3,
    item: "Rent",
    icon: "🏠",
    image: null,
    options: [
      { label: "$1,500 / month", amount: 1500 },
    ],
  },
  // ──── ADD NEW BILLS BELOW THIS LINE ────
  // {
  //   id: 4,
  //   item: "Bill Name",
  //   icon: "💡",
  //   image: null,
  //   options: [
  //     { label: "$100 / week", amount: 100 },
  //     { label: "$400 / month", amount: 400 },
  //   ],
  // },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: LEADERBOARD SETTINGS
 * ═══════════════════════════════════════════════════════════════════
 * The leaderboard is now AUTOMATICALLY populated from user accounts.
 * Users earn rank by logging in daily (login streaks).
 * Tiers: Gold (top 3), Silver (4-7), Bronze (8+)
 * Everything resets on the 1st of each month.
 *
 * EXCLUDED ACCOUNTS: Add usernames to hide from leaderboard
 * These accounts won't appear even if they have streaks.
 * ═══════════════════════════════════════════════════════════════════ */
const EXCLUDED_USERNAMES = ["nautx", "goddess selene", "goddessselene", "admin"];

/* Countries with flag emojis */
const COUNTRIES = [
  {n:"United States",f:"🇺🇸"},{n:"United Kingdom",f:"🇬🇧"},{n:"Canada",f:"🇨🇦"},{n:"Australia",f:"🇦🇺"},
  {n:"Germany",f:"🇩🇪"},{n:"France",f:"🇫🇷"},{n:"Japan",f:"🇯🇵"},{n:"South Korea",f:"🇰🇷"},
  {n:"Brazil",f:"🇧🇷"},{n:"Mexico",f:"🇲🇽"},{n:"India",f:"🇮🇳"},{n:"China",f:"🇨🇳"},
  {n:"Italy",f:"🇮🇹"},{n:"Spain",f:"🇪🇸"},{n:"Netherlands",f:"🇳🇱"},{n:"Sweden",f:"🇸🇪"},
  {n:"Norway",f:"🇳🇴"},{n:"Denmark",f:"🇩🇰"},{n:"Finland",f:"🇫🇮"},{n:"Poland",f:"🇵🇱"},
  {n:"Russia",f:"🇷🇺"},{n:"Ukraine",f:"🇺🇦"},{n:"Turkey",f:"🇹🇷"},{n:"South Africa",f:"🇿🇦"},
  {n:"Nigeria",f:"🇳🇬"},{n:"Egypt",f:"🇪🇬"},{n:"Kenya",f:"🇰🇪"},{n:"Morocco",f:"🇲🇦"},
  {n:"Argentina",f:"🇦🇷"},{n:"Colombia",f:"🇨🇴"},{n:"Chile",f:"🇨🇱"},{n:"Peru",f:"🇵🇪"},
  {n:"Venezuela",f:"🇻🇪"},{n:"Philippines",f:"🇵🇭"},{n:"Thailand",f:"🇹🇭"},{n:"Vietnam",f:"🇻🇳"},
  {n:"Indonesia",f:"🇮🇩"},{n:"Malaysia",f:"🇲🇾"},{n:"Singapore",f:"🇸🇬"},{n:"Taiwan",f:"🇹🇼"},
  {n:"New Zealand",f:"🇳🇿"},{n:"Ireland",f:"🇮🇪"},{n:"Scotland",f:"🏴󠁧󠁢󠁳󠁣󠁴󠁿"},{n:"Belgium",f:"🇧🇪"},
  {n:"Austria",f:"🇦🇹"},{n:"Switzerland",f:"🇨🇭"},{n:"Portugal",f:"🇵🇹"},{n:"Greece",f:"🇬🇷"},
  {n:"Czech Republic",f:"🇨🇿"},{n:"Romania",f:"🇷🇴"},{n:"Hungary",f:"🇭🇺"},{n:"Croatia",f:"🇭🇷"},
  {n:"Serbia",f:"🇷🇸"},{n:"Bulgaria",f:"🇧🇬"},{n:"Slovakia",f:"🇸🇰"},{n:"Slovenia",f:"🇸🇮"},
  {n:"Lithuania",f:"🇱🇹"},{n:"Latvia",f:"🇱🇻"},{n:"Estonia",f:"🇪🇪"},{n:"Iceland",f:"🇮🇸"},
  {n:"Israel",f:"🇮🇱"},{n:"Saudi Arabia",f:"🇸🇦"},{n:"UAE",f:"🇦🇪"},{n:"Qatar",f:"🇶🇦"},
  {n:"Kuwait",f:"🇰🇼"},{n:"Jordan",f:"🇯🇴"},{n:"Lebanon",f:"🇱🇧"},{n:"Iraq",f:"🇮🇶"},
  {n:"Iran",f:"🇮🇷"},{n:"Pakistan",f:"🇵🇰"},{n:"Bangladesh",f:"🇧🇩"},{n:"Sri Lanka",f:"🇱🇰"},
  {n:"Nepal",f:"🇳🇵"},{n:"Myanmar",f:"🇲🇲"},{n:"Cambodia",f:"🇰🇭"},{n:"Jamaica",f:"🇯🇲"},
  {n:"Trinidad & Tobago",f:"🇹🇹"},{n:"Dominican Republic",f:"🇩🇴"},{n:"Puerto Rico",f:"🇵🇷"},
  {n:"Cuba",f:"🇨🇺"},{n:"Costa Rica",f:"🇨🇷"},{n:"Panama",f:"🇵🇦"},{n:"Guatemala",f:"🇬🇹"},
  {n:"Honduras",f:"🇭🇳"},{n:"El Salvador",f:"🇸🇻"},{n:"Ecuador",f:"🇪🇨"},{n:"Bolivia",f:"🇧🇴"},
  {n:"Paraguay",f:"🇵🇾"},{n:"Uruguay",f:"🇺🇾"},{n:"Ghana",f:"🇬🇭"},{n:"Tanzania",f:"🇹🇿"},
  {n:"Ethiopia",f:"🇪🇹"},{n:"Algeria",f:"🇩🇿"},{n:"Tunisia",f:"🇹🇳"},{n:"Libya",f:"🇱🇾"},
  {n:"Senegal",f:"🇸🇳"},{n:"Cameroon",f:"🇨🇲"},{n:"Congo",f:"🇨🇩"},{n:"Angola",f:"🇦🇴"},
  {n:"Mozambique",f:"🇲🇿"},{n:"Zimbabwe",f:"🇿🇼"},{n:"Zambia",f:"🇿🇲"},{n:"Uganda",f:"🇺🇬"},
  {n:"Mongolia",f:"🇲🇳"},{n:"Kazakhstan",f:"🇰🇿"},{n:"Georgia",f:"🇬🇪"},{n:"Armenia",f:"🇦🇲"},
  {n:"Luxembourg",f:"🇱🇺"},{n:"Malta",f:"🇲🇹"},{n:"Cyprus",f:"🇨🇾"},{n:"Fiji",f:"🇫🇯"},
].sort((a,b) => a.n.localeCompare(b.n));

/* Role-based username colors */
function getRoleColor(role, username) {
  const lowerName = (username || "").toLowerCase();
  if (lowerName === "goddess selene" || lowerName === "goddessselene" || lowerName === "goddess-selene") return "#DC143C";
  if (role === "admin") return "#60a5fa";
  if (role === "moderator") return "#FFD700";
  if (role === "vip") return "#c026d3";
  if (role === "guest") return "rgba(245,230,232,.35)";
  return "#ff6b8a"; // sub (default)
}

function getRoleBadge(role, username) {
  const lowerName = (username || "").toLowerCase();
  if (lowerName === "goddess selene" || lowerName === "goddessselene" || lowerName === "goddess-selene") return "👑";
  if (role === "admin") return "💎";
  if (role === "moderator") return "⭐";
  if (role === "vip") return "💜";
  return null;
}

/* Assigns tier based on rank position */
function assignTier(rank) {
  if (rank <= 3) return { tier: "Gold", badge: "🥇" };
  if (rank <= 7) return { tier: "Silver", badge: "🥈" };
  return { tier: "Bronze", badge: "🥉" };
}

/* Empty fallback when no users exist yet */
const LEADERBOARD = [];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: WHEEL OPTIONS (25 segments — NO free passes)
 * ═══════════════════════════════════════════════════════════════════
 * ALL segments require the sub to DO something — no free rides!
 *
 * COLOR PALETTE:
 *   #DC143C (crimson)  #8B0000 (dark red)  #C71585 (violet red)
 *   #B22222 (firebrick) #AD1457 (dark pink) #880E4F (deep magenta)
 *   #4A0012 (midnight cherry) #2a0a0a (near black)
 * ═══════════════════════════════════════════════════════════════════ */
const WHEEL_SEGMENTS = [
  { label: "$5 Tribute", color: "#DC143C" },
  { label: "$10 Tribute", color: "#8B0000" },
  { label: "$15 Tribute", color: "#C71585" },
  { label: "$20 Tribute", color: "#4A0012" },
  { label: "$25 Tribute", color: "#B22222" },
  { label: "$30 Tribute", color: "#AD1457" },
  { label: "$40 Tribute", color: "#880E4F" },
  { label: "$50 Tribute", color: "#DC143C" },
  { label: "$75 Tribute", color: "#8B0000" },
  { label: "$100 Tribute", color: "#C71585" },
  { label: "Double Last Tribute", color: "#4A0012" },
  { label: "Buy Throne Gift", color: "#B22222" },
  { label: "Cover an Expense", color: "#AD1457" },
  { label: "Worship Video", color: "#880E4F" },
  { label: "Write 20 Compliments", color: "#DC143C" },
  { label: "1hr Wallet Drain", color: "#C71585" },
  { label: "Social Media Worship", color: "#4A0012" },
  { label: "Surprise CashApp", color: "#B22222" },
  { label: "Confess a Secret", color: "#AD1457" },
  { label: "Send a Haul Tribute", color: "#880E4F" },
  { label: "Complete 3 Tasks", color: "#DC143C" },
  { label: "Tip Every Post", color: "#8B0000" },
  { label: "Triple Tribute", color: "#C71585" },
  { label: "Spoil Goddess Today", color: "#4A0012" },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: TASKS FOR SUBS (25 tasks)
 * ═══════════════════════════════════════════════════════════════════
 * DIFFICULTY: "Easy", "Medium", "Hard"
 * ICONS: 🌅 💬 ⏱️ 📱 🎁 💳 🧎 📝 🎥 🖼️ 💰 🔥 ❤️ 🏃 🕯️ 📣 🛒 🪞 💌 🎶
 * ═══════════════════════════════════════════════════════════════════ */
const TASKS = [
  { id: 1, title: "Morning Devotion", desc: "Send a tribute within 5 minutes of waking up to start your day right.", difficulty: "Easy", icon: "🌅" },
  { id: 2, title: "Compliment Cascade", desc: "Write 10 unique compliments for Goddess Selene. Each one must be different.", difficulty: "Easy", icon: "💬" },
  { id: 3, title: "Wallet Drain Timer", desc: "Set a 1-hour timer. Every 15 minutes, send a tribute.", difficulty: "Medium", icon: "⏱️" },
  { id: 4, title: "Social Worship", desc: "Like and comment on every post Goddess has made this week.", difficulty: "Easy", icon: "📱" },
  { id: 5, title: "Wishlist Surprise", desc: "Purchase an item from the Throne wishlist without being told which one.", difficulty: "Hard", icon: "🎁" },
  { id: 6, title: "Expense Sponsor", desc: "Choose an expense from the reimbursement page and cover it fully.", difficulty: "Hard", icon: "💳" },
  { id: 7, title: "Devotion Journal", desc: "Write a full-page journal entry about why you serve Goddess Selene.", difficulty: "Easy", icon: "📝" },
  { id: 8, title: "Tribute Roulette", desc: "Spin the wheel 3 times and honor every single result. No exceptions.", difficulty: "Hard", icon: "🎰" },
  { id: 9, title: "Selfless Saturday", desc: "Dedicate your entire Saturday earnings to Goddess Selene.", difficulty: "Hard", icon: "💰" },
  { id: 10, title: "Mantra Repetition", desc: "Write 'I exist to serve Goddess Selene' 50 times and submit proof.", difficulty: "Medium", icon: "🪞" },
  { id: 11, title: "Fan Art Offering", desc: "Create a piece of art, edit, or moodboard dedicated to Goddess.", difficulty: "Medium", icon: "🖼️" },
  { id: 12, title: "Song Dedication", desc: "Find a song that captures your devotion and explain why in 200+ words.", difficulty: "Easy", icon: "🎶" },
  { id: 13, title: "Throne Haul", desc: "Buy 3 or more items from the Throne wishlist in a single session.", difficulty: "Hard", icon: "🛒" },
  { id: 14, title: "Daily Check-In", desc: "Message Goddess every morning for 7 days straight with a tribute.", difficulty: "Medium", icon: "📣" },
  { id: 15, title: "Public Declaration", desc: "Post on social media about your devotion to Goddess Selene.", difficulty: "Medium", icon: "📱" },
  { id: 16, title: "Love Letter", desc: "Write a heartfelt letter of worship and admiration. Minimum 500 words.", difficulty: "Medium", icon: "💌" },
  { id: 17, title: "Silent Worship", desc: "Send a tribute every hour for 8 hours without saying a single word.", difficulty: "Hard", icon: "🤫" },
  { id: 18, title: "Profile Makeover", desc: "Change your social media bio to declare your devotion to Goddess.", difficulty: "Easy", icon: "✏️" },
  { id: 19, title: "Gratitude List", desc: "List 25 things you're grateful to Goddess Selene for. Be specific.", difficulty: "Easy", icon: "🙏" },
  { id: 20, title: "Budget Sacrifice", desc: "Cut one personal luxury this week and tribute the savings instead.", difficulty: "Medium", icon: "✂️" },
  { id: 21, title: "Wake-Up Call", desc: "Set your alarm 30 minutes early for a week to send morning tributes.", difficulty: "Medium", icon: "⏰" },
  { id: 22, title: "Content Booster", desc: "Share, like, and repost every piece of Goddess content for 3 days.", difficulty: "Easy", icon: "🔄" },
  { id: 23, title: "Confession Booth", desc: "Submit 3 confessions on the Confessionals page. Be honest.", difficulty: "Easy", icon: "🕯️" },
  { id: 24, title: "VIP Treatment", desc: "Cover Goddess' most expensive listed expense without hesitation.", difficulty: "Hard", icon: "💎" },
  { id: 25, title: "Full Surrender", desc: "Complete 5 other tasks from this list in a single day.", difficulty: "Hard", icon: "🔥" },
  { id: 26, title: "Payday Offering", desc: "The moment you get paid, send the first tribute before spending on anything else.", difficulty: "Medium", icon: "🏦" },
  { id: 27, title: "Screenshot Proof", desc: "Screenshot your sent tributes for the week and submit them as proof of devotion.", difficulty: "Easy", icon: "📸" },
  { id: 28, title: "Impulse Denial", desc: "Every time you want to buy something for yourself today, tribute that amount instead.", difficulty: "Hard", icon: "🚫" },
  { id: 29, title: "Nighttime Ritual", desc: "Send a tribute and a goodnight message to Goddess every night for 5 days.", difficulty: "Medium", icon: "🌙" },
  { id: 30, title: "Referral Recruit", desc: "Bring a new devotee to the site. They must create an account and accept a task.", difficulty: "Medium", icon: "👥" },
  { id: 31, title: "Ultimate Devotion", desc: "Complete 10 tasks this month and send proof of each one. The ultimate test.", difficulty: "Hard", icon: "🏆" },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: FREE CONTENT
 * ═══════════════════════════════════════════════════════════════════
 * type: "photo", "video", or "audio"
 * locked: false = free, true = premium lock overlay
 * image: URL string or null
 * ═══════════════════════════════════════════════════════════════════ */
const FREE_CONTENT = [
  { id: 1, type: "photo", title: "Cherry Aesthetic", desc: "A taste of the Goddess aesthetic", locked: false, image: null },
  { id: 2, type: "photo", title: "Red Velvet Mood", desc: "Curated mood board visuals", locked: false, image: null },
  { id: 3, type: "photo", title: "Divine Preview", desc: "Preview of premium content", locked: false, image: null },
  { id: 4, type: "video", title: "Welcome Message", desc: "A personal welcome from Goddess", locked: false, image: null },
  { id: 5, type: "photo", title: "Luxe Lifestyle", desc: "Living the life you fund", locked: true, image: null },
  { id: 6, type: "audio", title: "Voice Note", desc: "An exclusive audio clip", locked: true, image: null },
  // ──── ADD NEW CONTENT BELOW ────
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: NAV ITEMS
 * ═══════════════════════════════════════════════════════════════════ */
const NAV_ITEMS = [
  { id: "home", label: "Home", icon: "♔" },
  { id: "links", label: "Links", icon: "🔗" },
  { id: "expenses", label: "Reimburse", icon: "💸" },
  { id: "content", label: "Content", icon: "📸" },
  { id: "games", label: "Games", icon: "🎰" },
  { id: "leaderboard", label: "Leaderboard", icon: "🏆" },
  { id: "chat", label: "Chat", icon: "💬" },
  { id: "throne", label: "Throne", icon: "🎁" },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: SOCIAL LINKS (shown on Home page bottom)
 * ═══════════════════════════════════════════════════════════════════
 * Replace "#" with actual URLs. These show as buttons on the home page.
 * ═══════════════════════════════════════════════════════════════════ */
const SOCIAL_LINKS = [
  { label: "Linktree", url: "https://linktr.ee/selenedommexo" },
  { label: "X / Twitter", url: "https://x.com/selenedommexo" },
  { label: "TikTok", url: "https://www.tiktok.com/@selenedommexo/video/7607267103212686622" },
  { label: "LoyalFans", url: "https://www.loyalfans.com/selenedommex" },
  { label: "Chaturbate", url: "https://chaturbate.com/luvrxstarx/" },
  { label: "Throne", url: THRONE_URL },
  // ──── ADD NEW LINKS BELOW ────
  // { label: "CashApp", url: "https://cash.app/$YourTag" },
  // { label: "Instagram", url: "https://instagram.com/YourHandle" },
];

/* ═══════════════════════════════════════════════════════════════════
 * 🔧 EDIT: LINK PAGE — Dedicated links page with descriptions & icons
 * ═══════════════════════════════════════════════════════════════════
 * Each link has: label, url, desc (short description), icon (emoji),
 * and color (gradient background for the card).
 *
 * To ADD: copy a block, paste before ], update values
 * To REMOVE: delete the { ... }, block
 * ═══════════════════════════════════════════════════════════════════ */
const LINK_PAGE_ITEMS = [
  {
    label: "Linktree",
    url: "https://linktr.ee/selenedommexo",
    desc: "All my links in one place",
    icon: "🌳",
    color: "linear-gradient(135deg, #43e97b, #38f9d7)",
  },
  {
    label: "X / Twitter",
    url: "https://x.com/selenedommexo",
    desc: "Follow my thoughts & updates",
    icon: "𝕏",
    color: "linear-gradient(135deg, #1a1a2e, #16213e)",
  },
  {
    label: "TikTok",
    url: "https://www.tiktok.com/@selenedommexo/video/7607267103212686622",
    desc: "Watch my latest content",
    icon: "🎵",
    color: "linear-gradient(135deg, #000000, #ee1d52)",
  },
  {
    label: "LoyalFans",
    url: "https://www.loyalfans.com/selenedommex",
    desc: "Exclusive premium content",
    icon: "⭐",
    color: "linear-gradient(135deg, #0061ff, #60efff)",
  },
  {
    label: "Chaturbate",
    url: "https://chaturbate.com/luvrxstarx/",
    desc: "Live sessions & shows",
    icon: "🎥",
    color: "linear-gradient(135deg, #f97316, #eab308)",
  },
  {
    label: "Throne Wishlist",
    url: THRONE_URL,
    desc: "Send gifts & fulfill wishes",
    icon: "👑",
    color: "linear-gradient(135deg, #6366f1, #ec4899)",
  },
  // ──── ADD NEW LINK PAGE ITEMS BELOW ────
  // {
  //   label: "Platform Name",
  //   url: "https://your-url.com",
  //   desc: "Short description",
  //   icon: "🔗",
  //   color: "linear-gradient(135deg, #color1, #color2)",
  // },
];


// ═══════════════════════════════════════════════════════════════════
//  COMPONENTS — Generally don't edit below unless customizing design
// ═══════════════════════════════════════════════════════════════════

// ─── Throne Logo (actual brand asset, base64-embedded) ───────────
/* 🔧 EDIT: THRONE LOGO
 * This is the official Throne gradient logo (light version for dark backgrounds).
 * To swap it out, replace the THRONE_LOGO_B64 string with a new base64-encoded image.
 * Or replace the <img> tag below with: <img src="/images/throne-logo.png" ... />
 */
const THRONE_LOGO_B64 = "data:image/jpeg;base64,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";

function ThroneLogo({ width = 260 }) {
  return (
    <img
      src={THRONE_LOGO_B64}
      alt="Throne"
      style={{ width, height: "auto", display: "block", margin: "0 auto" }}
    />
  );
}

// ─── Spin Wheel (25 segments, canvas-rendered) ───────────────────
function SpinWheel() {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const canvasRef = useRef(null);
  const segCount = WHEEL_SEGMENTS.length;
  const segAngle = 360 / segCount;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const size = 320;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 150;
    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);

    WHEEL_SEGMENTS.forEach((seg, i) => {
      const startA = ((i * segAngle - 90) * Math.PI) / 180;
      const endA = (((i + 1) * segAngle - 90) * Math.PI) / 180;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startA, endA);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();
      ctx.strokeStyle = "rgba(10,0,0,.6)";
      ctx.lineWidth = 1;
      ctx.stroke();

      const midA = (startA + endA) / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(midA);
      ctx.fillStyle = "#fff";
      ctx.font = "bold 7px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const words = seg.label.split(" ");
      if (words.length <= 2) {
        ctx.fillText(seg.label, radius * 0.66, 0);
      } else {
        ctx.fillText(words.slice(0, Math.ceil(words.length / 2)).join(" "), radius * 0.66, -5);
        ctx.fillText(words.slice(Math.ceil(words.length / 2)).join(" "), radius * 0.66, 5);
      }
      ctx.restore();
    });

    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fillStyle = "#1a0505";
    ctx.fill();
    ctx.strokeStyle = "#DC143C";
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = "#DC143C";
    ctx.font = "18px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("♔", cx, cy + 1);
  }, []);

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    const extra = Math.floor(Math.random() * 360);
    const spins = 360 * 6 + extra;
    const newRot = rotation + spins;
    setRotation(newRot);
    setTimeout(() => {
      const finalAngle = (360 - (newRot % 360) + 90) % 360;
      const idx = Math.floor(finalAngle / segAngle) % segCount;
      setResult(WHEEL_SEGMENTS[idx].label);
      setSpinning(false);
    }, 5000);
  };

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ position: "relative", width: 320, height: 320, margin: "0 auto" }}>
        <div style={{
          position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
          width: 0, height: 0, borderLeft: "14px solid transparent", borderRight: "14px solid transparent",
          borderTop: "28px solid #fff", zIndex: 10, filter: "drop-shadow(0 2px 8px rgba(0,0,0,.6))"
        }} />
        <canvas ref={canvasRef} style={{
          width: 320, height: 320,
          transition: "transform 5s cubic-bezier(0.15, 0.60, 0.08, 1.00)",
          transform: `rotate(${rotation}deg)`,
          borderRadius: "50%",
          boxShadow: "0 0 40px rgba(220,20,60,.3), inset 0 0 20px rgba(0,0,0,.3)"
        }} />
      </div>
      <button onClick={spin} disabled={spinning} style={{
        marginTop: 24, padding: "14px 52px",
        background: spinning ? "#444" : "linear-gradient(135deg, #DC143C, #8B0000)",
        color: "#fff", border: "none", borderRadius: 50, fontSize: 16, fontWeight: 700,
        cursor: spinning ? "not-allowed" : "pointer", fontFamily: "'Cormorant Garamond', serif",
        letterSpacing: 2, textTransform: "uppercase",
        boxShadow: spinning ? "none" : "0 4px 24px rgba(220,20,60,.5)",
        transition: "all .3s"
      }}>
        {spinning ? "✦ Spinning... ✦" : "Spin the Wheel"}
      </button>
      {result && (
        <div style={{
          marginTop: 20, padding: "16px 28px", background: "rgba(220,20,60,.12)",
          border: "2px solid rgba(220,20,60,.5)", borderRadius: 14,
          color: "#ff6b8a", fontSize: 20, fontWeight: 700,
          fontFamily: "'Playfair Display', serif", animation: "fadeIn .5s ease",
          boxShadow: "0 0 30px rgba(220,20,60,.15)"
        }}>
          🎰 <span style={{ color: "#DC143C" }}>{result}</span>
          <div style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 6, fontFamily: "'Cormorant Garamond', serif" }}>
            You must obey. No exceptions.
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Main App ────────────────────────────────────────────────────
function GoddessSelene() {
  const { user: clerkUser } = useUser();
  const [activeSection, setActiveSection] = useState("home");
  const [confessional, setConfessional] = useState("");
  const [confessName, setConfessName] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [confessions, setConfessions] = useState([]);
  const [pendingConfessions, setPendingConfessions] = useState([]);
  const [dbReady, setDbReady] = useState(false);
  const [dbContent, setDbContent] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({ title: "", description: "", type: "photo", is_locked: false, price: "", file: null });
  const [uploadError, setUploadError] = useState(null);
  const [contentViewer, setContentViewer] = useState(null); // { title, image_url, description, type }
  const [editingPrice, setEditingPrice] = useState(null); // { contentId, price }
  const [uploading, setUploading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null); // user profile being viewed
  const [myProfile, setMyProfile] = useState(null);
  const [profileEdit, setProfileEdit] = useState({ country: "", country_flag: "", age: "", display_name: "" });
  const [userRole, setUserRole] = useState("user");
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [chatUsers, setChatUsers] = useState({}); // userId -> { username, role }
  const [isBanned, setIsBanned] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [acceptedTasks, setAcceptedTasks] = useState({}); // { taskId: "accepted" | "completed" }

  /* ═══════════════════════════════════════════════════════════════
   * SUPABASE: Load confessions, accepted tasks, and leaderboard
   * These load from the database on mount and stay in sync.
   * ═══════════════════════════════════════════════════════════════ */
  const [dbLeaderboard, setDbLeaderboard] = useState(null);

  /* Payment Method Popup State */
  const [paymentPopup, setPaymentPopup] = useState(null); // null or { item, amount }

  /* 🔧 EDIT: PAYMENT LINKS
   * Replace these URLs with your actual payment links.
   * For CashApp: use your $cashtag URL
   * For Venmo: use your Venmo profile URL
   * For Throne: uses the locked THRONE_URL above
   */
  const PAYMENT_LINKS = {
    cashapp: "https://cash.app/$kittygurlx222",
    venmo: "https://venmo.com/stargirlxo21",
    throne: THRONE_URL,
  };

  /* ═══════════════════════════════════════════════════════════════
   * 🔧 EDIT: AGE VERIFICATION
   * Set to true to enable the age gate modal on first visit.
   * Set to false to disable it.
   * ═══════════════════════════════════════════════════════════════ */
  // Bridge for profile dropdown to open profile modal
  useEffect(() => {
    window.__openProfile = () => setShowProfile(true);
    return () => { delete window.__openProfile; };
  }, []);

  const AGE_GATE_ENABLED = true;
  /* NOTE FOR DEPLOYMENT: When hosted on Vercel/Netlify, you can persist the
   * age gate across page loads by changing the line below to:
   *   const [ageVerified, setAgeVerified] = useState(() => {
   *     if (!AGE_GATE_ENABLED) return true;
   *     try { return sessionStorage.getItem("gs_age_verified") === "true"; } catch { return false; }
   *   });
   * And in verifyAge, add: try { sessionStorage.setItem("gs_age_verified", "true"); } catch {}
   */
  const [ageVerified, setAgeVerified] = useState(!AGE_GATE_ENABLED);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);


  const verifyAge = () => {
    setAgeVerified(true);
    // Don't set tosAccepted yet — ToS popup will show next
  };

  const acceptTos = () => {
    setTosAccepted(true);
  };

  const denyAge = () => {
    try { window.location.href = "https://www.google.com"; } catch {}
  };

  /* Re-render every 30 seconds so "time ago" labels stay accurate */
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  /* Reusable: refresh leaderboard from user_profiles */
  const refreshLeaderboard = useCallback(async () => {
    try {
      const { data: profileData } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("is_excluded", false)
        .order("current_streak", { ascending: false })
        .limit(25);
      if (profileData && profileData.length > 0) {
        const ranked = profileData
          .filter(p => !EXCLUDED_USERNAMES.includes(p.username.toLowerCase()))
          .map((p, i) => {
            const tierInfo = assignTier(i + 1);
            return {
              rank: i + 1,
              name: p.nickname || p.username,
              tier: tierInfo.tier,
              badge: tierInfo.badge,
              streak: p.current_streak,
            };
          });
        setDbLeaderboard(ranked);
      }
    } catch (err) {
      console.log("Leaderboard refresh failed");
    }
  }, []);

  /* Load data from Supabase on mount */
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load approved confessions (visible to everyone)
        const { data: confData } = await supabase
          .from("confessions")
          .select("*")
          .eq("status", "approved")
          .order("created_at", { ascending: false })
          .limit(50);
        if (confData) {
          setConfessions(confData.map(c => ({
            text: c.text, author: c.author,
            timestamp: new Date(c.created_at).getTime(), id: c.id,
          })));
        }

        // Load pending confessions (for mod/admin panels)
        const { data: pendingData } = await supabase
          .from("confessions")
          .select("*")
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(50);
        if (pendingData) {
          setPendingConfessions(pendingData.map(c => ({
            text: c.text, author: c.author,
            timestamp: new Date(c.created_at).getTime(), id: c.id,
          })));
        }

        // Load leaderboard
        await refreshLeaderboard();

        // Load content items
        const { data: contentData, error: contentErr } = await supabase
          .from("content_items")
          .select("*")
          .order("sort_order", { ascending: true });
        if (contentErr) {
          console.log("Content table not found, using fallback:", contentErr.message);
        } else {
          setDbContent(contentData || []);
        }

        setDbReady(true);
      } catch (err) {
        console.log("Supabase not configured yet, using local state");
        setDbReady(true);
      }
    };
    loadData();
  }, []);

  /* ═══════════════════════════════════════════════════════════════
   * LOGIN STREAK TRACKER
   * Records daily login, manages streak count.
   * Called once per session when user is authenticated.
   * ═══════════════════════════════════════════════════════════════ */
  const recordLogin = useCallback(async (clerkUser) => {
    if (!clerkUser) return;
    const userId = clerkUser.id;
    const username = clerkUser.username || clerkUser.firstName || 
      clerkUser.emailAddresses?.[0]?.emailAddress?.split("@")[0] || "Devotee";
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    try {
      // Check if user profile exists
      const { data: existing } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("clerk_user_id", userId)
        .single();

      if (!existing) {
        // First visit — create profile
        const isExcluded = EXCLUDED_USERNAMES.includes(username.toLowerCase());
        await supabase.from("user_profiles").insert({
          clerk_user_id: userId,
          username: username,
          current_streak: 1,
          best_streak: 1,
          last_login_date: today,
          streak_reset_month: currentMonth,
          streak_reset_year: currentYear,
          is_excluded: isExcluded,
        });
      } else {
        // Returning user — check if streak should reset (new month)
        const needsMonthlyReset = existing.streak_reset_month !== currentMonth || 
          existing.streak_reset_year !== currentYear;

        if (needsMonthlyReset) {
          // New month — reset streak
          await supabase.from("user_profiles")
            .update({
              current_streak: 1,
              last_login_date: today,
              streak_reset_month: currentMonth,
              streak_reset_year: currentYear,
              username: username,
            })
            .eq("clerk_user_id", userId);
        } else if (existing.last_login_date !== today) {
          // Same month, different day
          const lastLogin = new Date(existing.last_login_date);
          const todayDate = new Date(today);
          const diffDays = Math.floor((todayDate - lastLogin) / (1000 * 60 * 60 * 24));

          let newStreak;
          if (diffDays === 1) {
            // Consecutive day — increment streak
            newStreak = existing.current_streak + 1;
          } else {
            // Missed a day — reset to 1
            newStreak = 1;
          }

          await supabase.from("user_profiles")
            .update({
              current_streak: newStreak,
              best_streak: Math.max(newStreak, existing.best_streak),
              last_login_date: today,
              username: username,
            })
            .eq("clerk_user_id", userId);
        }
        // If last_login_date === today, do nothing (already logged today)
      }
      // Load full profile
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("clerk_user_id", userId)
        .single();
      if (profile) {
        setUserRole(profile.role || "user");
        setUserProfile(profile);
      }

      // Cache all user roles for chat coloring
      const { data: allUsers } = await supabase
        .from("user_profiles")
        .select("clerk_user_id, username, role");
      if (allUsers) {
        const map = {};
        allUsers.forEach(u => { map[u.clerk_user_id] = { username: u.username, role: u.role }; });
        setChatUsers(map);
      }

      // Check ban
      const { data: banData } = await supabase
        .from("user_bans")
        .select("id")
        .eq("user_id", userId)
        .eq("is_active", true)
        .limit(1);
      if (banData && banData.length > 0) setIsBanned(true);

    } catch (err) {
      console.log("Could not record login:", err);
    }
  }, []);

  /* Load user's accepted tasks from Supabase (current month only) */
  const loadUserTasks = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const { data } = await supabase
        .from("accepted_tasks")
        .select("task_id, status")
        .eq("user_id", userId)
        .gte("accepted_at", monthStart);
      if (data) {
        const taskMap = {};
        data.forEach(t => { taskMap[t.task_id] = t.status || "accepted"; });
        setAcceptedTasks(taskMap);
      }
    } catch (err) {
      console.log("Could not load tasks from Supabase");
    }
  }, []);

  /* Load ALL users' tasks for admin view (current month) */
  const [allUserTasks, setAllUserTasks] = useState([]);
  const loadAllTasks = useCallback(async () => {
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const { data } = await supabase
        .from("accepted_tasks")
        .select("*")
        .gte("accepted_at", monthStart)
        .order("accepted_at", { ascending: false });
      if (data) setAllUserTasks(data);
    } catch (err) {
      console.log("Could not load all tasks");
    }
  }, []);

  /* Track login streak and load user tasks when Clerk user is available */
  useEffect(() => {
    if (clerkUser) {
      recordLogin(clerkUser);
      loadUserTasks(clerkUser.id);
    }
  }, [clerkUser, recordLogin, loadUserTasks]);


  /* Real-time: listen for content changes */
  useEffect(() => {
    const channel = supabase
      .channel("content-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "content_items" }, (p) => {
        setDbContent(prev => (prev && Array.isArray(prev)) ? [...prev, p.new] : [p.new]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "content_items" }, (p) => {
        setDbContent(prev => prev ? prev.map(c => c.id === p.new.id ? p.new : c) : [p.new]);
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "content_items" }, (p) => {
        setDbContent(prev => prev ? prev.filter(c => c.id !== p.old.id) : []);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  /* Real-time: listen for task status updates (admin completing tasks) */
  useEffect(() => {
    if (!clerkUser) return;
    const channel = supabase
      .channel("task-updates")
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "accepted_tasks",
        filter: `user_id=eq.${clerkUser.id}`,
      }, (payload) => {
        setAcceptedTasks(prev => ({ ...prev, [payload.new.task_id]: payload.new.status }));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [clerkUser]);

  /* Real-time: confessions — route to correct list based on status */
  useEffect(() => {
    const channel = supabase
      .channel("confessions-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "confessions" }, (p) => {
        const c = p.new;
        const mapped = { text: c.text, author: c.author, timestamp: new Date(c.created_at).getTime(), id: c.id };
        if (c.status === "approved") {
          setConfessions(prev => prev.some(e => e.id === c.id) ? prev : [mapped, ...prev]);
        } else if (c.status === "pending") {
          setPendingConfessions(prev => prev.some(e => e.id === c.id) ? prev : [mapped, ...prev]);
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "confessions" }, (p) => {
        const c = p.new;
        const mapped = { text: c.text, author: c.author, timestamp: new Date(c.created_at).getTime(), id: c.id };
        if (c.status === "approved") {
          setPendingConfessions(prev => prev.filter(e => e.id !== c.id));
          setConfessions(prev => prev.some(e => e.id === c.id) ? prev : [mapped, ...prev]);
        } else if (c.status === "denied") {
          setPendingConfessions(prev => prev.filter(e => e.id !== c.id));
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "confessions" }, (p) => {
        setConfessions(prev => prev.filter(e => e.id !== p.old.id));
        setPendingConfessions(prev => prev.filter(e => e.id !== p.old.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  /* Real-time: leaderboard — re-rank when streaks change */
  useEffect(() => {
    const channel = supabase
      .channel("leaderboard-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "user_profiles" }, () => {
        refreshLeaderboard();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refreshLeaderboard]);

  /* Real-time: bans — instant ban enforcement */
  useEffect(() => {
    if (!clerkUser) return;
    const channel = supabase
      .channel("bans-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "user_bans" }, (p) => {
        if (p.new.user_id === clerkUser.id && p.new.is_active) {
          setIsBanned(true);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [clerkUser]);

  /* Real-time: mutes — instant mute enforcement in chat */
  useEffect(() => {
    if (!clerkUser) return;
    const channel = supabase
      .channel("mutes-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "user_mutes" }, (p) => {
        if (p.new.user_id === clerkUser.id) {
          // Check if mute is still active
          if (new Date(p.new.muted_until) > new Date()) {
            // Force re-render of chat to show mute state
            window.__chatMuteRefresh && window.__chatMuteRefresh();
          }
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [clerkUser]);

  const [confessionSubmitted, setConfessionSubmitted] = useState(false);

  const submitConfession = async () => {
    if (!confessional.trim()) return;
    const author = isAnonymous ? "Anonymous" : (confessName.trim() || "Anonymous");
    setConfessional("");
    setConfessName("");

    try {
      await supabase.from("confessions").insert({
        text: confessional.trim(),
        author: author,
        status: "pending",
      });
      setConfessionSubmitted(true);
      setTimeout(() => setConfessionSubmitted(false), 3000);
    } catch (err) {
      console.log("Could not save confession to Supabase");
    }
  };

  /* Approve/deny/delete confessions (mod + admin) */
  const approveConfession = async (id) => {
    await supabase.from("confessions").update({ status: "approved" }).eq("id", id);
    const item = pendingConfessions.find(c => c.id === id);
    setPendingConfessions(prev => prev.filter(c => c.id !== id));
    if (item) setConfessions(prev => [item, ...prev]);
  };

  const denyConfession = async (id) => {
    await supabase.from("confessions").update({ status: "denied" }).eq("id", id);
    setPendingConfessions(prev => prev.filter(c => c.id !== id));
  };

  const deleteConfession = async (id) => {
    await supabase.from("confessions").delete().eq("id", id);
    setConfessions(prev => prev.filter(c => c.id !== id));
    setPendingConfessions(prev => prev.filter(c => c.id !== id));
  };

  /* ═══════════════════════════════════════════════════════════════
   * CONTENT MANAGEMENT (Admin Only)
   * Upload images to Supabase Storage, save metadata to database
   * ═══════════════════════════════════════════════════════════════ */
  const uploadContent = async () => {
    if (!uploadData.title.trim() || uploading) return;
    setUploading(true);
    setUploadError(null);

    try {
      let imageUrl = null;

      // Upload file to Supabase Storage if provided
      if (uploadData.file) {
        const fileExt = uploadData.file.name.split(".").pop().toLowerCase();
        const fileName = `content-${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

        const { error: storageError } = await supabase.storage
          .from("content-images")
          .upload(fileName, uploadData.file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (storageError) {
          console.error("Storage upload error:", storageError);
          setUploadError("Image upload failed: " + storageError.message + ". Make sure the 'content-images' bucket exists and is set to PUBLIC in Supabase Storage.");
          setUploading(false);
          return;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from("content-images")
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }

      // Save to database
      const insertData = {
        title: uploadData.title.trim(),
        description: uploadData.description.trim(),
        type: uploadData.type,
        is_locked: uploadData.is_locked,
        image_url: imageUrl,
        sort_order: dbContent ? dbContent.length : 0,
      };
      if (uploadData.is_locked && uploadData.price) {
        insertData.price = parseFloat(uploadData.price) || 0;
      }

      const { data: newItem, error: insertError } = await supabase
        .from("content_items")
        .insert(insertData)
        .select()
        .single();

      if (insertError) {
        console.error("Database insert error:", insertError);
        setUploadError("Database save failed: " + insertError.message + ". Make sure you ran the content SQL schema.");
        setUploading(false);
        return;
      }

      if (newItem) {
        setDbContent(prev => (prev && Array.isArray(prev)) ? [...prev, newItem] : [newItem]);
      }

      // Reset form
      setUploadData({ title: "", description: "", type: "photo", is_locked: false, price: "", file: null });
      setShowUploadForm(false);
      setUploadError(null);
    } catch (err) {
      console.error("Content upload failed:", err);
      setUploadError("Upload failed: " + (err.message || "Unknown error"));
    }
    setUploading(false);
  };

  const deleteContent = async (contentId) => {
    try {
      await supabase.from("content_items").delete().eq("id", contentId);
      setDbContent(prev => prev ? prev.filter(c => c.id !== contentId) : []);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  /* Save profile fields that don't need approval (country, age) */
  const saveProfileDirect = async () => {
    if (!clerkUser) return;
    const countryObj = COUNTRIES.find(c => c.n === profileEdit.country);
    try {
      await supabase.from("user_profiles").update({
        country: profileEdit.country,
        country_flag: countryObj?.f || "",
        age: profileEdit.age ? parseInt(profileEdit.age) : null,
      }).eq("clerk_user_id", clerkUser.id);
      setMyProfile(prev => ({ ...prev, country: profileEdit.country, country_flag: countryObj?.f || "", age: profileEdit.age ? parseInt(profileEdit.age) : null }));
    } catch (err) { console.error("Profile save failed:", err); }
  };

  /* Request profile changes that need admin approval (display_name, profile_pic) */
  const requestProfileChange = async (fieldName, newValue) => {
    if (!clerkUser || !newValue.trim()) return;
    const username = myProfile?.username || clerkUser.username || "User";
    try {
      await supabase.from("profile_change_requests").insert({
        user_id: clerkUser.id,
        username: username,
        field_name: fieldName,
        current_value: myProfile?.[fieldName] || "",
        requested_value: newValue.trim(),
        status: "pending",
      });
    } catch (err) { console.error("Profile request failed:", err); }
  };

  /* Load any user's profile for viewing */
  const loadUserProfile = async (userId) => {
    try {
      const { data } = await supabase.from("user_profiles").select("*").eq("clerk_user_id", userId).single();
      if (data) setViewingProfile(data);
    } catch (err) { console.log("Could not load profile"); }
  };

  const toggleContentLock = async (contentId, currentLock) => {
    try {
      await supabase.from("content_items").update({ is_locked: !currentLock }).eq("id", contentId);
      setDbContent(prev => prev ? prev.map(c => c.id === contentId ? { ...c, is_locked: !currentLock } : c) : []);
    } catch (err) {
      console.error("Toggle lock failed:", err);
    }
  };

  const updateContentPrice = async (contentId, newPrice) => {
    try {
      const price = parseFloat(newPrice) || 0;
      await supabase.from("content_items").update({ price }).eq("id", contentId);
      setDbContent(prev => prev ? prev.map(c => c.id === contentId ? { ...c, price } : c) : []);
      setEditingPrice(null);
    } catch (err) {
      console.error("Price update failed:", err);
    }
  };

  const acceptTask = async (taskId, userId) => {
    setAcceptedTasks(prev => ({ ...prev, [taskId]: "accepted" }));
    try {
      const username = clerkUser?.username || clerkUser?.firstName || "Devotee";
      await supabase.from("accepted_tasks").insert({
        user_id: userId || "unknown",
        username: username,
        task_id: taskId,
        status: "accepted",
      });
    } catch (err) {
      console.log("Could not save task to Supabase");
    }
  };

  /* Admin-only: Mark a task as completed for a specific user */
  const completeTask = async (taskId, userId) => {
    try {
      await supabase.from("accepted_tasks")
        .update({ status: "completed" })
        .eq("user_id", userId)
        .eq("task_id", taskId);
      // If it's the current user's task, update local state
      if (userId === clerkUser?.id) {
        setAcceptedTasks(prev => ({ ...prev, [taskId]: "completed" }));
      }
    } catch (err) {
      console.log("Could not complete task");
    }
  };

  /* Use database leaderboard if loaded, otherwise use empty fallback */
  const activeLeaderboard = dbLeaderboard || LEADERBOARD;

  const tierColors = {
    Gold: "linear-gradient(135deg, #FFD700, #FFA500)",
    Silver: "linear-gradient(135deg, #C0C0C0, #E8E8E8)",
    Bronze: "linear-gradient(135deg, #CD7F32, #DAA520)",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0000", color: "#f5e6e8", fontFamily: "'Cormorant Garamond', serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(220,20,60,.3); } 50% { box-shadow: 0 0 40px rgba(220,20,60,.6); } }
        @keyframes crownFloat { 0%,100% { transform: rotate(-3deg) translateY(0); } 50% { transform: rotate(3deg) translateY(-6px); } }
        @keyframes checkPop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
        @keyframes borderGlow { 0%, 100% { border-color: rgba(76,175,80,.3); } 50% { border-color: rgba(76,175,80,.6); } }
        @keyframes linkHover { 0% { background-position: 0% 50%; } 100% { background-position: 100% 50%; } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #1a0505; }
        ::-webkit-scrollbar-thumb { background: #DC143C; border-radius: 3px; }
        .nav-item { cursor: pointer; padding: 10px 20px; border-radius: 8px; transition: all .25s; display: flex; align-items: center; gap: 8px; font-size: 15px; white-space: nowrap; }
        .nav-item:hover { background: rgba(220,20,60,.15); }
        .nav-item.active { background: rgba(220,20,60,.25); color: #ff4466; }
        .card { background: linear-gradient(135deg, rgba(26,5,5,.9), rgba(40,8,8,.9)); border: 1px solid rgba(220,20,60,.2); border-radius: 16px; padding: 24px; transition: all .3s; position: relative; overflow: hidden; }
        .card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(220,20,60,.5), transparent); }
        .card:hover { border-color: rgba(220,20,60,.5); transform: translateY(-2px); box-shadow: 0 8px 32px rgba(220,20,60,.15); }
        .card-accepted { border-color: rgba(76,175,80,.4) !important; background: linear-gradient(135deg, rgba(10,30,10,.9), rgba(20,40,15,.9)) !important; animation: borderGlow 2s ease infinite; }
        .btn-primary { padding: 12px 32px; background: linear-gradient(135deg, #DC143C, #8B0000); color: #fff; border: none; border-radius: 50px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: 'Cormorant Garamond', serif; letter-spacing: 1.5px; text-transform: uppercase; transition: all .3s; box-shadow: 0 4px 20px rgba(220,20,60,.4); }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 28px rgba(220,20,60,.6); }
        .btn-outline { padding: 10px 24px; background: transparent; color: #DC143C; border: 1.5px solid rgba(220,20,60,.5); border-radius: 50px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'Cormorant Garamond', serif; letter-spacing: 1px; text-transform: uppercase; transition: all .3s; text-decoration: none; display: inline-block; }
        .btn-outline:hover { background: rgba(220,20,60,.1); border-color: #DC143C; }
        .section-title { font-family: 'Playfair Display', serif; font-size: 36px; font-weight: 900; background: linear-gradient(135deg, #DC143C, #ff6b8a, #DC143C); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shimmer 3s linear infinite; margin-bottom: 8px; }
        .expense-row { display: flex; align-items: center; gap: 16px; padding: 16px 20px; border-radius: 12px; transition: all .25s; cursor: pointer; border: 1px solid transparent; }
        .expense-row:hover { background: rgba(220,20,60,.08); border-color: rgba(220,20,60,.2); }
        .confess-input { width: 100%; padding: 16px 20px; background: rgba(26,5,5,.8); border: 1px solid rgba(220,20,60,.3); border-radius: 12px; color: #f5e6e8; font-family: 'Cormorant Garamond', serif; font-size: 16px; resize: vertical; min-height: 100px; outline: none; transition: border-color .3s; }
        .confess-input:focus { border-color: #DC143C; }
        .confess-input::placeholder { color: rgba(245,230,232,.3); }
        .name-input { padding: 12px 16px; background: rgba(26,5,5,.8); border: 1px solid rgba(220,20,60,.3); border-radius: 10px; color: #f5e6e8; font-family: 'Cormorant Garamond', serif; font-size: 15px; outline: none; transition: border-color .3s; width: 100%; }
        .name-input:focus { border-color: #DC143C; }
        .name-input::placeholder { color: rgba(245,230,232,.3); }
        .rank-badge { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 16px; color: #1a0505; }
        .mobile-nav-toggle { display: none; position: fixed; top: 16px; right: 16px; z-index: 1000; width: 44px; height: 44px; background: rgba(220,20,60,.9); border: none; border-radius: 12px; color: #fff; font-size: 22px; cursor: pointer; }
        .game-tab { padding: 12px 24px; border-radius: 50px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Cormorant Garamond', serif; letter-spacing: 1px; text-transform: uppercase; transition: all .3s; border: 1.5px solid transparent; }
        .content-card { border-radius: 16px; overflow: hidden; position: relative; cursor: pointer; transition: all .3s; border: 1px solid rgba(220,20,60,.15); }
        .content-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(220,20,60,.2); }
        .link-card { border-radius: 16px; overflow: hidden; transition: all .3s; cursor: pointer; border: 1px solid rgba(255,255,255,.08); text-decoration: none; display: block; }
        .link-card:hover { transform: translateY(-4px) scale(1.01); box-shadow: 0 16px 48px rgba(0,0,0,.4); border-color: rgba(255,255,255,.15); }
        .toggle-track { width: 44px; height: 24px; border-radius: 12px; cursor: pointer; transition: all .3s; position: relative; border: none; padding: 0; }
        .toggle-knob { width: 18px; height: 18px; border-radius: 50%; background: #fff; position: absolute; top: 3px; transition: all .3s; box-shadow: 0 1px 4px rgba(0,0,0,.3); }
        @media (max-width: 768px) {
          .mobile-nav-toggle { display: flex; align-items: center; justify-content: center; }
          .desktop-nav { display: none !important; }
          .section-title { font-size: 28px !important; }
          .main-content { padding: 16px !important; }
        }
        @media (min-width: 769px) { .mobile-overlay { display: none !important; } }
        .version-tag { position: fixed; bottom: 8px; right: 12px; font-size: 10px; color: rgba(245,230,232,.15); letter-spacing: 1px; z-index: 5; font-family: monospace; }
      `}</style>

      <div className="version-tag">v5.4</div>

      {/* ═══════ MY PROFILE MODAL ═══════ */}
      {showProfile && myProfile && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9997,
          background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setShowProfile(false)}>
          <div style={{
            maxWidth: 440, width: "100%", maxHeight: "90vh", overflowY: "auto",
            padding: "32px 28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(220,20,60,.3)", boxShadow: "0 20px 60px rgba(0,0,0,.6)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%", margin: "0 auto 12px",
                background: "linear-gradient(135deg, #DC143C, #8B0000)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 32, color: "#fff", fontWeight: 700,
                border: "3px solid rgba(220,20,60,.5)",
              }}>{(myProfile.display_name || myProfile.username || "D").charAt(0).toUpperCase()}</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#ff6b8a" }}>
                {myProfile.display_name || myProfile.username}
              </div>
              {myProfile.country_flag && <span style={{ fontSize: 20 }}>{myProfile.country_flag}</span>}
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginTop: 4 }}>
                Member since {new Date(myProfile.created_at).toLocaleDateString()}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Display Name (requires approval) */}
              <div>
                <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, display: "block" }}>
                  Display Name <span style={{ color: "#ffa726", fontSize: 10 }}>(needs approval)</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={profileEdit.display_name} onChange={e => setProfileEdit(p => ({ ...p, display_name: e.target.value }))}
                    placeholder="Your display name" maxLength={24}
                    style={{ flex: 1, padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif" }}
                  />
                  <button onClick={() => { requestProfileChange("display_name", profileEdit.display_name); }}
                    style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "rgba(255,152,0,.2)", color: "#ffa726", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif", whiteSpace: "nowrap" }}>
                    Request
                  </button>
                </div>
              </div>

              {/* Country (direct save) */}
              <div>
                <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, display: "block" }}>Country</label>
                <select value={profileEdit.country} onChange={e => setProfileEdit(p => ({ ...p, country: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif", cursor: "pointer" }}>
                  <option value="">Select country...</option>
                  {COUNTRIES.map(c => <option key={c.n} value={c.n}>{c.f} {c.n}</option>)}
                </select>
              </div>

              {/* Age (direct save) */}
              <div>
                <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, display: "block" }}>Age</label>
                <input type="number" min={18} max={99} value={profileEdit.age} onChange={e => setProfileEdit(p => ({ ...p, age: e.target.value }))}
                  placeholder="18+"
                  style={{ width: "100%", padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif" }}
                />
              </div>

              {/* Save direct fields */}
              <button onClick={() => { saveProfileDirect(); setShowProfile(false); }} className="btn-primary" style={{ padding: "14px", fontSize: 14, width: "100%" }}>
                Save Profile
              </button>
            </div>

            <button onClick={() => setShowProfile(false)} style={{
              width: "100%", marginTop: 12, padding: "10px", background: "transparent",
              border: "1px solid rgba(220,20,60,.2)", borderRadius: 10, color: "rgba(245,230,232,.4)",
              fontSize: 13, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
            }}>Close</button>
          </div>
        </div>
      )}

      {/* ═══════ VIEWING ANOTHER USER'S PROFILE ═══════ */}
      {viewingProfile && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9997,
          background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setViewingProfile(null)}>
          <div style={{
            maxWidth: 380, width: "100%", padding: "32px 28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(220,20,60,.3)", textAlign: "center",
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", margin: "0 auto 12px",
              background: viewingProfile.role === "admin" ? "linear-gradient(135deg, #60a5fa, #3b82f6)" : viewingProfile.role === "moderator" ? "linear-gradient(135deg, #FFD700, #FFA500)" : "linear-gradient(135deg, #DC143C, #8B0000)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 32, color: "#fff", fontWeight: 700,
              border: "3px solid rgba(220,20,60,.5)",
            }}>{(viewingProfile.display_name || viewingProfile.username || "D").charAt(0).toUpperCase()}</div>

            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: getRoleColor(viewingProfile.role, viewingProfile.username), marginBottom: 4 }}>
              {viewingProfile.display_name || viewingProfile.username}
            </div>

            {getRoleBadge(viewingProfile.role, viewingProfile.username) && (
              <span style={{
                display: "inline-block", fontSize: 10, padding: "3px 10px", borderRadius: 20, fontWeight: 700, letterSpacing: 1, marginBottom: 8,
                background: "rgba(220,20,60,.15)",
                color: getRoleColor(viewingProfile.role, viewingProfile.username),
              }}>{getRoleBadge(viewingProfile.role, viewingProfile.username)}</span>
            )}

            <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 16, marginBottom: 16 }}>
              {viewingProfile.country_flag && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 24 }}>{viewingProfile.country_flag}</div>
                  <div style={{ fontSize: 11, color: "rgba(245,230,232,.4)", marginTop: 2 }}>{viewingProfile.country}</div>
                </div>
              )}
              {viewingProfile.age && (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#f5e6e8" }}>{viewingProfile.age}</div>
                  <div style={{ fontSize: 11, color: "rgba(245,230,232,.4)", marginTop: 2 }}>Age</div>
                </div>
              )}
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#DC143C" }}>{viewingProfile.current_streak}</div>
                <div style={{ fontSize: 11, color: "rgba(245,230,232,.4)", marginTop: 2 }}>Day Streak</div>
              </div>
            </div>

            <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)", marginBottom: 16 }}>
              Joined {new Date(viewingProfile.created_at).toLocaleDateString()}
            </div>

            <button onClick={() => setViewingProfile(null)} style={{
              width: "100%", padding: "12px", background: "transparent",
              border: "1px solid rgba(220,20,60,.2)", borderRadius: 10, color: "rgba(245,230,232,.4)",
              fontSize: 13, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
            }}>Close</button>
          </div>
        </div>
      )}

      {/* ═══════ CONTENT VIEWER LIGHTBOX ═══════ */}
      {contentViewer && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9997,
          background: "rgba(0,0,0,.95)", backdropFilter: "blur(16px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, cursor: "pointer",
        }} onClick={() => setContentViewer(null)}>
          <div style={{ maxWidth: "90vw", maxHeight: "90vh", position: "relative" }} onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <button onClick={() => setContentViewer(null)} style={{
              position: "absolute", top: -16, right: -16, width: 36, height: 36, borderRadius: "50%",
              background: "rgba(220,20,60,.8)", border: "none", color: "#fff", fontSize: 18,
              cursor: "pointer", zIndex: 1, boxShadow: "0 4px 12px rgba(0,0,0,.5)",
            }}>✕</button>
            <img src={contentViewer.image_url} alt={contentViewer.title} style={{
              maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,.5)",
            }} />
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#ff6b8a" }}>{contentViewer.title}</div>
              {contentViewer.description && (
                <div style={{ fontSize: 14, color: "rgba(245,230,232,.5)", marginTop: 6 }}>{contentViewer.description}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ ADMIN PRICE EDITOR MODAL ═══════ */}
      {editingPrice && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
          background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setEditingPrice(null)}>
          <div style={{
            maxWidth: 340, width: "100%", padding: "28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(220,20,60,.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 28 }}>💲</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#ff6b8a", marginTop: 8 }}>Set Price</div>
              <p style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginTop: 4 }}>Set to 0 or leave empty for no price</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 22, fontWeight: 900, color: "#DC143C" }}>$</span>
              <input
                type="number" min="0" step="1" placeholder="0"
                value={editingPrice.price}
                onChange={e => setEditingPrice(prev => ({ ...prev, price: e.target.value }))}
                autoFocus
                style={{
                  flex: 1, padding: "12px 16px", background: "rgba(10,0,0,.5)",
                  border: "1px solid rgba(220,20,60,.3)", borderRadius: 10,
                  color: "#f5e6e8", fontSize: 20, fontWeight: 700, outline: "none",
                  fontFamily: "'Playfair Display', serif", textAlign: "center",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditingPrice(null)} style={{
                flex: 1, padding: "10px", background: "transparent", border: "1px solid rgba(220,20,60,.2)",
                borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer",
                fontFamily: "'Cormorant Garamond', serif",
              }}>Cancel</button>
              <button onClick={() => updateContentPrice(editingPrice.contentId, editingPrice.price)} className="btn-primary" style={{
                flex: 1, padding: "10px", fontSize: 13, borderRadius: 10,
              }}>Save Price</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ PAYMENT METHOD POPUP ═══════ */}
      {paymentPopup && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
          background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, animation: "fadeIn .3s ease"
        }} onClick={() => setPaymentPopup(null)}>
          <div style={{
            maxWidth: 420, width: "100%", padding: "36px 28px",
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            borderRadius: 20, border: "1px solid rgba(220,20,60,.3)",
            boxShadow: "0 20px 60px rgba(220,20,60,.2)",
            animation: "fadeIn .3s ease"
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💸</div>
              <div style={{
                fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900,
                color: "#ff6b8a", marginBottom: 4
              }}>{paymentPopup.item}</div>
              <div style={{
                fontSize: 28, fontWeight: 900, color: "#DC143C",
                fontFamily: "'Playfair Display', serif"
              }}>${paymentPopup.amount.toLocaleString()}</div>
            </div>

            <div style={{
              fontSize: 13, color: "rgba(245,230,232,.5)", textAlign: "center",
              marginBottom: 20, letterSpacing: 1, textTransform: "uppercase"
            }}>Choose Payment Method</div>

            {/* Payment Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* CashApp */}
              <a href={PAYMENT_LINKS.cashapp} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  padding: "16px 20px", borderRadius: 14, cursor: "pointer",
                  background: "linear-gradient(135deg, #00D632, #00A825)",
                  display: "flex", alignItems: "center", gap: 14,
                  transition: "all .3s", border: "1px solid transparent"
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,.2)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22
                  }}>💵</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif" }}>CashApp</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>Send via CashApp</div>
                  </div>
                  <div style={{ color: "rgba(255,255,255,.7)", fontSize: 18 }}>→</div>
                </div>
              </a>

              {/* Venmo */}
              <a href={PAYMENT_LINKS.venmo} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  padding: "16px 20px", borderRadius: 14, cursor: "pointer",
                  background: "linear-gradient(135deg, #3D95CE, #008CFF)",
                  display: "flex", alignItems: "center", gap: 14,
                  transition: "all .3s", border: "1px solid transparent"
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,.2)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22
                  }}>💳</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif" }}>Venmo</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>Send via Venmo</div>
                  </div>
                  <div style={{ color: "rgba(255,255,255,.7)", fontSize: 18 }}>→</div>
                </div>
              </a>

              {/* Throne */}
              <a href={PAYMENT_LINKS.throne} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  padding: "16px 20px", borderRadius: 14, cursor: "pointer",
                  background: "linear-gradient(135deg, #6366f1, #c026d3)",
                  display: "flex", alignItems: "center", gap: 14,
                  transition: "all .3s", border: "1px solid transparent"
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,.2)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22
                  }}>👑</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif" }}>Throne</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>Gift via Throne wishlist</div>
                  </div>
                  <div style={{ color: "rgba(255,255,255,.7)", fontSize: 18 }}>→</div>
                </div>
              </a>
            </div>

            {/* CashApp Disclaimer */}
            <div style={{
              marginTop: 18, padding: "12px 16px", borderRadius: 10,
              background: "rgba(220,20,60,.08)", border: "1px solid rgba(220,20,60,.12)",
              fontSize: 11, color: "rgba(245,230,232,.5)", lineHeight: 1.5,
              textAlign: "center"
            }}>
              ⚠️ Do <span style={{ color: "#DC143C", fontWeight: 700 }}>NOT</span> use suggestive notes in the payment method to protect privacy and discretion.
            </div>

            {/* Close button */}
            <button onClick={() => setPaymentPopup(null)} style={{
              width: "100%", marginTop: 16, padding: "12px", background: "transparent",
              border: "1px solid rgba(220,20,60,.3)", borderRadius: 12,
              color: "rgba(245,230,232,.5)", fontSize: 13, cursor: "pointer",
              fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1,
              textTransform: "uppercase", transition: "all .3s"
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ═══════ PROFILE MODAL ═══════ */}
      {showProfileModal && userProfile && (
        <UserProfileModal
          profile={userProfile}
          clerkUser={clerkUser}
          userRole={userRole}
          onClose={() => setShowProfileModal(false)}
          onUpdate={(updated) => setUserProfile(updated)}
        />
      )}

      {/* ═══════ BANNED SCREEN ═══════ */}
      {isBanned && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
          background: "#0a0000", display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 16, padding: 20, textAlign: "center"
        }}>
          <div style={{ fontSize: 64 }}>🚫</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: "#DC143C" }}>Access Denied</div>
          <p style={{ color: "rgba(245,230,232,.5)", fontSize: 16, maxWidth: 400 }}>
            Your account has been suspended from Goddess Selene's realm.
          </p>
        </div>
      )}

      {/* ═══════ AGE VERIFICATION MODAL ═══════ */}
      {!ageVerified && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: "rgba(0,0,0,.95)", backdropFilter: "blur(20px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 20, padding: 20
        }}>
          <div style={{
            maxWidth: 440, textAlign: "center", padding: "48px 36px",
            background: "linear-gradient(135deg, rgba(26,5,5,.95), rgba(40,8,8,.95))",
            borderRadius: 24, border: "1px solid rgba(220,20,60,.3)",
            boxShadow: "0 20px 60px rgba(220,20,60,.15)"
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👑</div>
            <div style={{
              fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900,
              color: "#DC143C", marginBottom: 8
            }}>Age Verification</div>
            <p style={{
              fontSize: 16, color: "rgba(245,230,232,.6)", lineHeight: 1.6,
              marginBottom: 28
            }}>
              This website contains adult content. You must be 18 years or older to enter.
            </p>
            <div style={{ fontSize: 15, color: "rgba(245,230,232,.5)", marginBottom: 24 }}>
              Are you 18 or older?
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <button className="btn-primary" onClick={verifyAge} style={{ padding: "14px 40px", fontSize: 16 }}>
                Yes, I'm 18+
              </button>
              <button className="btn-outline" onClick={denyAge} style={{ padding: "14px 32px", fontSize: 16 }}>
                No, Exit
              </button>
            </div>
            <p style={{ fontSize: 11, color: "rgba(245,230,232,.25)", marginTop: 20, lineHeight: 1.5 }}>
              By entering, you confirm that you are at least 18 years of age.
            </p>
          </div>
        </div>
      )}

      {/* ═══════ TERMS OF SERVICE & PRIVACY POLICY MODAL ═══════ */}
      {ageVerified && !tosAccepted && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: "rgba(0,0,0,.95)", backdropFilter: "blur(20px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, overflow: "auto",
        }}>
          <div style={{
            maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto",
            padding: "36px 32px",
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            borderRadius: 24, border: "1px solid rgba(220,20,60,.3)",
            boxShadow: "0 20px 60px rgba(220,20,60,.15)",
          }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📜</div>
              <div style={{
                fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900,
                color: "#DC143C", marginBottom: 4,
              }}>Terms & Policies</div>
              <p style={{ fontSize: 13, color: "rgba(245,230,232,.4)" }}>
                Please read and accept before continuing
              </p>
            </div>

            {/* ── Terms of Service ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700,
                color: "#ff6b8a", marginBottom: 10,
                display: "flex", alignItems: "center", gap: 8,
              }}>📋 Terms of Service</div>
              <div style={{
                padding: "16px 18px", borderRadius: 12, maxHeight: 200, overflowY: "auto",
                background: "rgba(0,0,0,.3)", border: "1px solid rgba(220,20,60,.12)",
                fontSize: 13, color: "rgba(245,230,232,.6)", lineHeight: 1.7,
              }}>
                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>1. Acceptance of Terms</strong><br/>
                By accessing and using this website ("Site"), operated by Goddess Selene ("we," "us," "our"), you agree to be bound by these Terms of Service. If you do not agree, you must immediately leave the Site.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>2. Eligibility</strong><br/>
                You must be at least 18 years of age to access this Site. By using this Site, you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>3. Account Registration</strong><br/>
                To access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You agree to provide accurate and current information.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>4. Payments & Refund Policy</strong><br/>
                All payments made through this Site, including tributes, reimbursements, gifts, and subscriptions, are voluntary and final. <strong style={{ color: "#DC143C" }}>ALL SALES ARE FINAL. NO REFUNDS.</strong> By making a payment, you acknowledge that you are doing so willingly and without expectation of goods or services in return unless explicitly stated.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>5. User Conduct</strong><br/>
                You agree not to: use the Site for any unlawful purpose; harass, threaten, or abuse other users; post harmful, defamatory, or obscene content; attempt to gain unauthorized access to any part of the Site; impersonate any person or entity; or use automated systems to access the Site.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>6. Content & Intellectual Property</strong><br/>
                All content on this Site, including text, images, graphics, logos, and media, is the property of Goddess Selene and is protected by copyright and intellectual property laws. You may not reproduce, distribute, or create derivative works without explicit written permission.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>7. Account Termination</strong><br/>
                We reserve the right to suspend or terminate your account at any time, for any reason, including violation of these Terms. Banned users forfeit access to all Site features.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>8. Chat & Community Guidelines</strong><br/>
                The chat feature is monitored. Messages may be deleted and users may be muted or banned by moderators or administrators at their discretion. Respect all users and follow the community guidelines at all times.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>9. Disclaimer of Warranties</strong><br/>
                This Site is provided "as is" without warranties of any kind, express or implied. We do not guarantee that the Site will be uninterrupted, error-free, or secure.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>10. Limitation of Liability</strong><br/>
                To the maximum extent permitted by law, Goddess Selene shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Site.</p>

                <p><strong style={{ color: "#f5e6e8" }}>11. Changes to Terms</strong><br/>
                We reserve the right to modify these Terms at any time. Continued use of the Site after changes constitutes acceptance of the updated Terms.</p>
              </div>
            </div>

            {/* ── Privacy Policy ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700,
                color: "#ff6b8a", marginBottom: 10,
                display: "flex", alignItems: "center", gap: 8,
              }}>🔒 Privacy Policy</div>
              <div style={{
                padding: "16px 18px", borderRadius: 12, maxHeight: 200, overflowY: "auto",
                background: "rgba(0,0,0,.3)", border: "1px solid rgba(220,20,60,.12)",
                fontSize: 13, color: "rgba(245,230,232,.6)", lineHeight: 1.7,
              }}>
                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Information We Collect</strong><br/>
                When you create an account, we collect your email address and username. You may voluntarily provide additional profile information. We also collect basic usage data to operate the Site.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>How We Use Your Information</strong><br/>
                We use your information to: operate and maintain the Site; manage your account; facilitate community features; and enforce our Terms of Service.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Third-Party Services</strong><br/>
                We use trusted third-party service providers for authentication, data storage, hosting, and payment processing. These providers operate under their own privacy policies. We do not control and are not responsible for their data practices.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Data Security</strong><br/>
                Your data is protected using industry-standard security measures. We do not sell, trade, or rent your personal information. Payment processing is handled entirely by third-party processors — we never access, store, or handle your payment credentials.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Cookies</strong><br/>
                The Site uses cookies and similar technologies for session management. Third-party services may also use cookies for their functionality.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Your Rights</strong><br/>
                You may request deletion of your account and associated data by contacting us. You may update your profile information at any time. You may request information about the data we hold about you.</p>

                <p style={{ marginBottom: 10 }}><strong style={{ color: "#f5e6e8" }}>Data Retention</strong><br/>
                Account data is retained while your account is active. Certain user-generated content may be retained or automatically removed per our data policies.</p>

                <p><strong style={{ color: "#f5e6e8" }}>Contact</strong><br/>
                For privacy inquiries, please use the contact methods listed on the Site.</p>
              </div>
            </div>

            {/* ── Refund Policy Highlight ── */}
            <div style={{
              padding: "14px 18px", borderRadius: 12, marginBottom: 24,
              background: "rgba(220,20,60,.1)", border: "1px solid rgba(220,20,60,.25)",
              textAlign: "center",
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#DC143C", letterSpacing: 1 }}>
                💰 ALL SALES ARE FINAL — NO REFUNDS
              </div>
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.5)", marginTop: 4 }}>
                All tributes, payments, gifts, and reimbursements are non-refundable.
              </div>
            </div>

            {/* ── Accept Button ── */}
            <button className="btn-primary" onClick={acceptTos} style={{
              width: "100%", padding: "16px", fontSize: 16, marginBottom: 10,
            }}>
              I Accept — Enter the Realm
            </button>
            <button className="btn-outline" onClick={denyAge} style={{
              width: "100%", padding: "12px", fontSize: 14,
            }}>
              I Decline — Exit
            </button>
            <p style={{ fontSize: 10, color: "rgba(245,230,232,.2)", textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
              By clicking "I Accept," you agree to the Terms of Service, Privacy Policy, and Refund Policy above.
            </p>
          </div>
        </div>
      )}

      {/* ═══════ VIEW POLICIES MODAL (accessible anytime) ═══════ */}
      {showPolicies && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
          background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setShowPolicies(false)}>
          <div style={{
            maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto",
            padding: "32px 28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(220,20,60,.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, color: "#DC143C" }}>Terms & Policies</div>
            </div>

            <div style={{
              padding: "16px 18px", borderRadius: 12, maxHeight: 500, overflowY: "auto",
              background: "rgba(0,0,0,.3)", border: "1px solid rgba(220,20,60,.12)",
              fontSize: 13, color: "rgba(245,230,232,.6)", lineHeight: 1.7, marginBottom: 16,
            }}>
              <p style={{ marginBottom: 8 }}><strong style={{ color: "#ff6b8a", fontSize: 15 }}>Terms of Service</strong></p>
              <p style={{ marginBottom: 8 }}>All payments are voluntary and final. <strong style={{ color: "#DC143C" }}>NO REFUNDS.</strong> You must be 18+ to use this site. Your account may be terminated for violating community guidelines. Content is protected by copyright. The site is provided "as is" without warranties.</p>

              <p style={{ marginBottom: 8, marginTop: 16 }}><strong style={{ color: "#ff6b8a", fontSize: 15 }}>Privacy Policy</strong></p>
              <p style={{ marginBottom: 8 }}>We collect basic account info (email, username) and usage data. Additional profile details are optional. Data is stored securely using industry-standard measures. We do not sell your data. Payment info is handled entirely by third-party processors — we never store payment credentials. You may request account deletion at any time.</p>

              <p style={{ marginBottom: 8, marginTop: 16 }}><strong style={{ color: "#ff6b8a", fontSize: 15 }}>Refund Policy</strong></p>
              <p>All sales are final. All tributes, payments, gifts, reimbursements, and subscriptions are non-refundable under any circumstances.</p>
            </div>

            <button onClick={() => setShowPolicies(false)} className="btn-primary" style={{
              width: "100%", padding: "12px", fontSize: 14, borderRadius: 10,
            }}>Close</button>
          </div>
        </div>
      )}

      {/* Background */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: "-20%", right: "-10%", width: 600, height: 600, background: "radial-gradient(circle, rgba(220,20,60,.08) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "-10%", width: 500, height: 500, background: "radial-gradient(circle, rgba(139,0,0,.06) 0%, transparent 70%)", borderRadius: "50%" }} />
      </div>

      {/* User Profile Dropdown — top right */}
      <UserProfileDropdown onOpenProfile={() => setShowProfileModal(true)} profile={userProfile} />

      {/* Mobile Nav Toggle */}
      <button className="mobile-nav-toggle" onClick={() => setMobileNav(!mobileNav)} style={{ right: 78 }}>{mobileNav ? "✕" : "☰"}</button>

      {/* Mobile Nav Overlay */}
      {mobileNav && (
        <div className="mobile-overlay" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, background: "rgba(10,0,0,.95)", backdropFilter: "blur(20px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, animation: "fadeIn .3s ease" }}>
          <div style={{ fontSize: 32, marginBottom: 20, animation: "crownFloat 3s ease infinite" }}>♔</div>
          {NAV_ITEMS.map(item => (
            <div key={item.id} className={`nav-item ${activeSection === item.id ? 'active' : ''}`} style={{ fontSize: 20, padding: "14px 32px" }}
              onClick={() => { setActiveSection(item.id); setMobileNav(false); }}>
              <span>{item.icon}</span> {item.label}
            </div>
          ))}
          {(userRole === "moderator" || userRole === "admin") && (
            <div className={`nav-item ${activeSection === "modpanel" ? 'active' : ''}`} style={{ fontSize: 20, padding: "14px 32px" }}
              onClick={() => { setActiveSection("modpanel"); setMobileNav(false); }}>
              <span>🛡️</span> Mod Panel
            </div>
          )}
          {userRole === "admin" && (
            <div className={`nav-item ${activeSection === "admin" ? 'active' : ''}`} style={{ fontSize: 20, padding: "14px 32px" }}
              onClick={() => { setActiveSection("admin"); setMobileNav(false); }}>
              <span>⚙️</span> Admin
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}>

        {/* Sidebar */}
        <nav className="desktop-nav" style={{ width: 220, minHeight: "100vh", padding: "28px 12px", background: "linear-gradient(180deg, rgba(26,5,5,.95), rgba(15,2,2,.98))", borderRight: "1px solid rgba(220,20,60,.15)", position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ textAlign: "center", marginBottom: 28, paddingBottom: 20, borderBottom: "1px solid rgba(220,20,60,.15)" }}>
            <div style={{ fontSize: 36, marginBottom: 6, animation: "crownFloat 3s ease infinite" }}>♔</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 900, color: "#DC143C", letterSpacing: 2 }}>GODDESS</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900, background: "linear-gradient(135deg, #ff6b8a, #DC143C)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>SELENE</div>
          </div>
          {NAV_ITEMS.map(item => (
            <div key={item.id} className={`nav-item ${activeSection === item.id ? "active" : ""}`} onClick={() => setActiveSection(item.id)}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              <span style={{ fontWeight: 600 }}>{item.label}</span>
            </div>
          ))}
          {(userRole === "moderator" || userRole === "admin") && (
            <div className={`nav-item ${activeSection === "modpanel" ? "active" : ""}`} onClick={() => setActiveSection("modpanel")} style={{ marginTop: 8, borderTop: "1px solid rgba(220,20,60,.15)", paddingTop: 12 }}>
              <span style={{ fontSize: 18 }}>🛡️</span>
              <span style={{ fontWeight: 600, color: "#FFD700" }}>Mod Panel</span>
            </div>
          )}
          {userRole === "admin" && (
            <div className={`nav-item ${activeSection === "admin" ? "active" : ""}`} onClick={() => setActiveSection("admin")}>
              <span style={{ fontSize: 18 }}>⚙️</span>
              <span style={{ fontWeight: 600, color: "#DC143C" }}>Admin</span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ padding: "16px 12px", borderTop: "1px solid rgba(220,20,60,.15)", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "rgba(245,230,232,.3)", letterSpacing: 1, marginBottom: 6 }}>© 2026 GODDESS SELENE</div>
            <button onClick={() => setShowPolicies(true)} style={{
              background: "none", border: "none", color: "rgba(245,230,232,.25)", fontSize: 10,
              cursor: "pointer", textDecoration: "underline", fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: 0.5,
            }}>Terms · Privacy · Refund Policy</button>
          </div>
        </nav>

        {/* Main Content */}
        <main className="main-content" style={{ flex: 1, padding: "32px 40px", maxWidth: 960, margin: "0 auto" }}>

          {/* ═══════ HOME ═══════ */}
          {activeSection === "home" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div style={{ textAlign: "center", padding: "60px 20px 50px" }}>
                <div style={{ width: 130, height: 130, borderRadius: "50%", margin: "0 auto 28px", background: "linear-gradient(135deg, #DC143C, #8B0000, #C71585)", padding: 4, animation: "glow 3s ease infinite" }}>
                  <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: "#1a0505", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 52 }}>👑</div>
                </div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 56, fontWeight: 900, lineHeight: 1.1, marginBottom: 12 }}>
                  <span style={{ color: "#f5e6e8" }}>Goddess</span>{" "}
                  <span style={{ background: "linear-gradient(135deg, #DC143C, #ff6b8a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Selene</span>
                </h1>
                {/* 🔧 EDIT: TAGLINE */}
                <p style={{ fontSize: 20, color: "rgba(245,230,232,.6)", fontStyle: "italic", fontWeight: 300, maxWidth: 500, margin: "0 auto 32px", lineHeight: 1.6 }}>
                  Your devotion is my birthright. Kneel before perfection.
                </p>
                <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                  <button className="btn-primary" onClick={() => setActiveSection("expenses")}>Reimburse My Life</button>
                  <button className="btn-outline" onClick={() => setActiveSection("games")}>Play Games</button>
                  <button className="btn-outline" onClick={() => setActiveSection("links")}>My Links</button>
                </div>
              </div>

              {/* Quick Links */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginTop: 40 }}>
                {[
                  { icon: "🔗", title: "Links", desc: "All platforms", section: "links" },
                  { icon: "💸", title: "Reimburse", desc: "Fund my lifestyle", section: "expenses" },
                  { icon: "📸", title: "Content", desc: "Free media", section: "content" },
                  { icon: "🎰", title: "Games", desc: "Spin, confess, obey", section: "games" },
                  { icon: "🏆", title: "Leaderboard", desc: "Top devotees", section: "leaderboard" },
                  { icon: "🎁", title: "Throne", desc: "Wishlist & gifts", section: "throne" },
                ].map((link, i) => (
                  <div key={i} className="card" style={{ cursor: "pointer", textAlign: "center", padding: "24px 16px" }} onClick={() => setActiveSection(link.section)}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>{link.icon}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#ff6b8a", marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>{link.title}</div>
                    <div style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>{link.desc}</div>
                  </div>
                ))}
              </div>

              {/* 🔧 EDIT: LOYALFANS WIDGET — Embedded widget from LoyalFans */}
              <div style={{
                marginTop: 40, textAlign: "center", padding: "28px 20px",
                borderTop: "1px solid rgba(220,20,60,.15)"
              }}>
                <p style={{
                  fontSize: 14, color: "rgba(245,230,232,.4)", letterSpacing: 2,
                  textTransform: "uppercase", marginBottom: 12
                }}>Featured Content</p>
                <LoyalFansWidget />
              </div>

              {/* Social Links — bottom of home */}
              <div style={{ marginTop: 40, textAlign: "center", padding: "32px 20px", borderTop: "1px solid rgba(220,20,60,.15)" }}>
                <p style={{ fontSize: 14, color: "rgba(245,230,232,.4)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Follow & Worship</p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  {SOCIAL_LINKS.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <button className="btn-outline" style={{ fontSize: 12, padding: "8px 16px" }}>{s.label}</button>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══════ LINKS PAGE (Dedicated) ═══════ */}
          {activeSection === "links" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div className="section-title">Find Me Everywhere</div>
                <p style={{ color: "rgba(245,230,232,.5)", fontSize: 16 }}>All my platforms, one page. Tap to visit.</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 520, margin: "0 auto" }}>
                {LINK_PAGE_ITEMS.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="link-card"
                    style={{ animation: `fadeIn .5s ease ${i * 0.07}s both` }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 16, padding: "20px 24px",
                      background: link.color, position: "relative", overflow: "hidden"
                    }}>
                      {/* Subtle shine overlay */}
                      <div style={{
                        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                        background: "linear-gradient(135deg, rgba(255,255,255,.1) 0%, transparent 50%, rgba(0,0,0,.1) 100%)",
                        pointerEvents: "none"
                      }} />
                      <div style={{
                        width: 52, height: 52, borderRadius: 14, background: "rgba(0,0,0,.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 26, flexShrink: 0, backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,.1)"
                      }}>{link.icon}</div>
                      <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
                        <div style={{
                          fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700,
                          color: "#fff", marginBottom: 2, textShadow: "0 1px 3px rgba(0,0,0,.3)"
                        }}>{link.label}</div>
                        <div style={{
                          fontSize: 13, color: "rgba(255,255,255,.75)", fontFamily: "'Cormorant Garamond', serif"
                        }}>{link.desc}</div>
                      </div>
                      <div style={{
                        fontSize: 20, color: "rgba(255,255,255,.6)", position: "relative", zIndex: 1
                      }}>→</div>
                    </div>
                  </a>
                ))}
              </div>
              <div style={{ textAlign: "center", marginTop: 32, fontSize: 13, color: "rgba(245,230,232,.3)", fontStyle: "italic" }}>
                ♔ Goddess Selene — All Rights Reserved
              </div>
            </div>
          )}

          {/* ═══════ EXPENSES / REIMBURSE ═══════ */}
          {activeSection === "expenses" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div className="section-title">Reimburse My Life</div>
              <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 28, fontSize: 16, lineHeight: 1.6 }}>
                A Goddess shouldn't have to pay for anything. Choose an expense and prove your devotion.
              </p>

              {/* ── Expense Items ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {EXPENSES.map((exp, i) => (
                  <div key={exp.id} className="card" style={{
                    padding: "20px 24px", animation: `fadeIn .5s ease ${i * 0.06}s both`
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: exp.tiers ? 14 : 0 }}>
                      {exp.image ? (
                        <img src={exp.image} alt={exp.item} style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", border: "1px solid rgba(220,20,60,.2)", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 52, height: 52, borderRadius: 12, background: "rgba(220,20,60,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{exp.icon}</div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 17, color: "#f5e6e8", fontFamily: "'Playfair Display', serif" }}>{exp.item}</div>
                        {!exp.tiers && (
                          <div style={{ fontSize: 14, color: "rgba(245,230,232,.4)", marginTop: 2 }}>
                            Cover this expense
                          </div>
                        )}
                        {exp.tiers && (
                          <div style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 2 }}>
                            Choose your tribute amount
                          </div>
                        )}
                      </div>
                      {/* Single-price items show amount + button inline */}
                      {!exp.tiers && (
                        <>
                          <div style={{ fontSize: 22, fontWeight: 900, color: "#DC143C", fontFamily: "'Playfair Display', serif", marginRight: 8 }}>
                            ${exp.amount.toLocaleString()}
                          </div>
                          <button className="btn-primary" style={{ padding: "8px 20px", fontSize: 12, flexShrink: 0 }}
                            onClick={() => setPaymentPopup({ item: exp.item, amount: exp.amount })}>
                            Reimburse
                          </button>
                        </>
                      )}
                    </div>
                    {/* Tiered items show amount buttons below */}
                    {exp.tiers && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {exp.tiers.map((tier) => (
                          <button key={tier} className="btn-primary" style={{
                            padding: "10px 20px", fontSize: 13, flex: "1 1 auto", minWidth: 80
                          }} onClick={() => setPaymentPopup({ item: exp.item, amount: tier })}>
                            ${tier.toLocaleString()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* ── Adopt A Bill Section ── */}
              <div style={{ marginTop: 44 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, marginBottom: 8
                }}>
                  <div style={{
                    fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900,
                    background: "linear-gradient(135deg, #DC143C, #ff6b8a, #DC143C)",
                    backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    animation: "shimmer 3s linear infinite"
                  }}>Adopt A Bill</div>
                </div>
                <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
                  Take ownership of Goddess Selene's recurring expenses. Choose a bill and a payment schedule.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {ADOPT_BILLS.map((bill, i) => (
                    <div key={bill.id} className="card" style={{
                      padding: "22px 24px",
                      animation: `fadeIn .5s ease ${i * 0.08}s both`,
                      border: "1px solid rgba(220,20,60,.25)",
                      background: "linear-gradient(135deg, rgba(26,5,5,.95), rgba(50,10,15,.9))"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
                        {bill.image ? (
                          <img src={bill.image} alt={bill.item} style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", border: "1px solid rgba(220,20,60,.2)", flexShrink: 0 }} />
                        ) : (
                          <div style={{
                            width: 52, height: 52, borderRadius: 12,
                            background: "linear-gradient(135deg, rgba(220,20,60,.2), rgba(220,20,60,.08))",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0
                          }}>{bill.icon}</div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 18, color: "#f5e6e8", fontFamily: "'Playfair Display', serif" }}>
                            {bill.item}
                          </div>
                          <div style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 2 }}>
                            Choose a payment schedule
                          </div>
                        </div>
                        <div style={{
                          padding: "4px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                          letterSpacing: 1.5, textTransform: "uppercase",
                          background: "rgba(220,20,60,.15)", color: "#DC143C",
                          border: "1px solid rgba(220,20,60,.25)"
                        }}>Recurring</div>
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {bill.options.map((opt, j) => (
                          <button key={j} style={{
                            flex: "1 1 auto", minWidth: 140, padding: "12px 16px", borderRadius: 12,
                            background: "linear-gradient(135deg, rgba(220,20,60,.15), rgba(220,20,60,.05))",
                            border: "1.5px solid rgba(220,20,60,.35)", color: "#ff6b8a",
                            fontSize: 14, fontWeight: 700, cursor: "pointer",
                            fontFamily: "'Cormorant Garamond', serif",
                            transition: "all .3s", textAlign: "center"
                          }}
                          onClick={() => setPaymentPopup({ item: bill.item, amount: opt.amount })}
                          onMouseEnter={e => { e.target.style.background = "linear-gradient(135deg, #DC143C, #8B0000)"; e.target.style.color = "#fff"; e.target.style.borderColor = "transparent"; e.target.style.boxShadow = "0 4px 20px rgba(220,20,60,.4)"; }}
                          onMouseLeave={e => { e.target.style.background = "linear-gradient(135deg, rgba(220,20,60,.15), rgba(220,20,60,.05))"; e.target.style.color = "#ff6b8a"; e.target.style.borderColor = "rgba(220,20,60,.35)"; e.target.style.boxShadow = "none"; }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══════ CONTENT (Admin-uploadable) ═══════ */}
          {activeSection === "content" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
                <div className="section-title">Content</div>
                {userRole === "admin" && (
                  <button className="btn-primary" style={{ padding: "10px 24px", fontSize: 13 }}
                    onClick={() => setShowUploadForm(!showUploadForm)}>
                    {showUploadForm ? "✕ Cancel" : "＋ Upload Content"}
                  </button>
                )}
              </div>
              <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 24, fontSize: 16, lineHeight: 1.6 }}>
                A preview of perfection. Want more? You know what to do.
              </p>

              {/* ── Admin Upload Form ── */}
              {showUploadForm && userRole === "admin" && (
                <div className="card" style={{ marginBottom: 24, padding: "24px", border: "1px solid rgba(220,20,60,.4)" }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#ff6b8a", marginBottom: 16 }}>
                    📤 Upload New Content
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Title */}
                    <input
                      type="text" placeholder="Title"
                      value={uploadData.title}
                      onChange={e => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                      style={{
                        padding: "12px 16px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)",
                        borderRadius: 10, color: "#f5e6e8", fontSize: 15, outline: "none",
                        fontFamily: "'Cormorant Garamond', serif",
                      }}
                    />

                    {/* Description */}
                    <input
                      type="text" placeholder="Description (optional)"
                      value={uploadData.description}
                      onChange={e => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                      style={{
                        padding: "12px 16px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)",
                        borderRadius: 10, color: "#f5e6e8", fontSize: 15, outline: "none",
                        fontFamily: "'Cormorant Garamond', serif",
                      }}
                    />

                    {/* Type + Lock Row */}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <select
                        value={uploadData.type}
                        onChange={e => setUploadData(prev => ({ ...prev, type: e.target.value }))}
                        style={{
                          flex: 1, minWidth: 120, padding: "12px 16px", background: "rgba(10,0,0,.5)",
                          border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8",
                          fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif", cursor: "pointer",
                        }}
                      >
                        <option value="photo">📷 Photo</option>
                        <option value="video">🎬 Video</option>
                        <option value="audio">🎧 Audio</option>
                      </select>

                      <button
                        onClick={() => setUploadData(prev => ({ ...prev, is_locked: !prev.is_locked }))}
                        style={{
                          flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                          fontSize: 14, fontWeight: 600, fontFamily: "'Cormorant Garamond', serif",
                          background: uploadData.is_locked ? "rgba(220,20,60,.2)" : "rgba(76,175,80,.15)",
                          border: uploadData.is_locked ? "1px solid rgba(220,20,60,.4)" : "1px solid rgba(76,175,80,.3)",
                          color: uploadData.is_locked ? "#DC143C" : "#66bb6a",
                        }}
                      >
                        {uploadData.is_locked ? "🔒 Premium (Locked)" : "🔓 Free (Unlocked)"}
                      </button>
                    </div>

                    {/* Price field (only shown when locked/premium) */}
                    {uploadData.is_locked && (
                      <input
                        type="number" placeholder="Price (e.g. 25)" min="1" step="1"
                        value={uploadData.price}
                        onChange={e => setUploadData(prev => ({ ...prev, price: e.target.value }))}
                        style={{
                          padding: "12px 16px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)",
                          borderRadius: 10, color: "#f5e6e8", fontSize: 15, outline: "none",
                          fontFamily: "'Cormorant Garamond', serif",
                        }}
                      />
                    )}

                    {/* File Upload */}
                    <div style={{
                      padding: "20px", borderRadius: 12, border: "2px dashed rgba(220,20,60,.3)",
                      background: "rgba(220,20,60,.03)", textAlign: "center", cursor: "pointer",
                      position: "relative", overflow: "hidden",
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => setUploadData(prev => ({ ...prev, file: e.target.files[0] || null }))}
                        style={{
                          position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
                        }}
                      />
                      {uploadData.file ? (
                        <div style={{ color: "#ff6b8a", fontSize: 14 }}>
                          ✓ {uploadData.file.name} ({(uploadData.file.size / 1024 / 1024).toFixed(1)} MB)
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 28, marginBottom: 6 }}>📁</div>
                          <div style={{ color: "rgba(245,230,232,.5)", fontSize: 14 }}>
                            Click or drag to upload an image
                          </div>
                          <div style={{ color: "rgba(245,230,232,.3)", fontSize: 12, marginTop: 4 }}>
                            JPG, PNG, GIF, WebP
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    {/* Error message */}
                    {uploadError && (
                      <div style={{
                        padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.1)",
                        border: "1px solid rgba(220,20,60,.3)", color: "#DC143C", fontSize: 12, lineHeight: 1.5,
                      }}>
                        ⚠️ {uploadError}
                      </div>
                    )}

                    <button
                      onClick={uploadContent}
                      disabled={!uploadData.title.trim() || uploading}
                      className="btn-primary"
                      style={{
                        padding: "14px", fontSize: 14, width: "100%",
                        opacity: (!uploadData.title.trim() || uploading) ? 0.4 : 1,
                      }}
                    >
                      {uploading ? "Uploading..." : "Upload Content"}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Content Grid ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 20 }}>
                {(dbContent || FREE_CONTENT).map((item, i) => {
                  const imgUrl = item.image_url || item.image;
                  const isLocked = item.is_locked !== undefined ? item.is_locked : item.locked;
                  const itemType = item.type;
                  const itemTitle = item.title;
                  const itemDesc = item.description || item.desc || "";
                  const itemPrice = item.price;

                  const handleCardClick = (e) => {
                    // Don't open viewer if clicking admin buttons
                    if (e.target.closest("button")) return;
                    if (isLocked && userRole !== "admin") {
                      // Locked content — show payment popup
                      if (itemPrice) {
                        setPaymentPopup({ item: itemTitle, amount: itemPrice });
                      }
                      return;
                    }
                    // Open viewer for unlocked content (or admin viewing locked)
                    if (imgUrl) {
                      setContentViewer({ title: itemTitle, image_url: imgUrl, description: itemDesc, type: itemType });
                    }
                  };

                  return (
                    <div key={item.id} className="content-card" style={{ animation: `fadeIn .5s ease ${i * 0.1}s both`, position: "relative" }}
                      onClick={handleCardClick}>
                      <div style={{
                        height: 220,
                        background: imgUrl ? `url(${imgUrl}) center/cover` : isLocked ? "linear-gradient(135deg, #1a0505, #2a0a0a)" : `linear-gradient(${135 + i * 30}deg, #3a0a10, #1a0505, #2a0510)`,
                        display: "flex", alignItems: "center", justifyContent: "center", position: "relative"
                      }}>
                        {!imgUrl && <div style={{ fontSize: 48, opacity: isLocked ? 0.3 : 0.6 }}>{itemType === "photo" ? "📷" : itemType === "video" ? "🎬" : "🎧"}</div>}
                        {isLocked && (
                          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontSize: 32 }}>🔒</span>
                            <span style={{ fontSize: 12, color: "#DC143C", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>Premium</span>
                            {itemPrice ? (
                              <span style={{ fontSize: 20, fontWeight: 900, color: "#fff", fontFamily: "'Playfair Display', serif" }}>${itemPrice}</span>
                            ) : null}
                            {itemPrice ? (
                              <span style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginTop: 2 }}>Tap to unlock</span>
                            ) : null}
                          </div>
                        )}
                        <div style={{ position: "absolute", top: 12, right: 12, padding: "4px 12px", background: "rgba(220,20,60,.3)", borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#fff" }}>{itemType}</div>

                        {/* Admin controls overlay */}
                        {userRole === "admin" && dbContent && (
                          <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 4 }}>
                            <button onClick={(e) => { e.stopPropagation(); toggleContentLock(item.id, isLocked); }} title={isLocked ? "Unlock" : "Lock"} style={{
                              width: 28, height: 28, borderRadius: 6, border: "none", cursor: "pointer",
                              background: "rgba(0,0,0,.6)", color: "#fff", fontSize: 13, backdropFilter: "blur(4px)",
                            }}>{isLocked ? "🔓" : "🔒"}</button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingPrice({ contentId: item.id, price: item.price || "" }); }} title="Set Price" style={{
                              width: 28, height: 28, borderRadius: 6, border: "none", cursor: "pointer",
                              background: "rgba(255,215,0,.4)", color: "#fff", fontSize: 13,
                            }}>💲</button>
                            <button onClick={(e) => { e.stopPropagation(); if(window.confirm("Delete this content?")) deleteContent(item.id); }} title="Delete" style={{
                              width: 28, height: 28, borderRadius: 6, border: "none", cursor: "pointer",
                              background: "rgba(220,20,60,.6)", color: "#fff", fontSize: 13,
                            }}>🗑</button>
                          </div>
                        )}
                      </div>
                      <div style={{ padding: "16px 18px", background: "rgba(26,5,5,.9)" }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#f5e6e8", fontFamily: "'Playfair Display', serif" }}>{itemTitle}</div>
                        <div style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 4 }}>{itemDesc}</div>
                        {isLocked && itemPrice && (
                          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 16, fontWeight: 900, color: "#DC143C", fontFamily: "'Playfair Display', serif" }}>${itemPrice}</span>
                            <span style={{ fontSize: 11, color: "rgba(245,230,232,.3)", letterSpacing: 1, textTransform: "uppercase" }}>Premium</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Empty state */}
              {dbContent && dbContent.length === 0 && (
                <div className="card" style={{ textAlign: "center", padding: "40px 24px", marginTop: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📸</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#ff6b8a", marginBottom: 8 }}>
                    No Content Yet
                  </div>
                  <p style={{ fontSize: 14, color: "rgba(245,230,232,.5)" }}>
                    {userRole === "admin" ? 'Click "Upload Content" above to add your first post.' : "Content coming soon. Stay devoted."}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ═══════ GAMES ═══════ */}
          {activeSection === "games" && (
            <GamesSection
              confessional={confessional} setConfessional={setConfessional}
              confessName={confessName} setConfessName={setConfessName}
              isAnonymous={isAnonymous} setIsAnonymous={setIsAnonymous}
              confessions={confessions} submitConfession={submitConfession}
              deleteConfession={deleteConfession} confessionSubmitted={confessionSubmitted}
              acceptedTasks={acceptedTasks} acceptTask={(taskId) => acceptTask(taskId, clerkUser?.id)}
              userRole={userRole} completeTask={completeTask} clerkUserId={clerkUser?.id}
            />
          )}

          {/* ═══════ LEADERBOARD ═══════ */}
          {activeSection === "leaderboard" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div className="section-title">Devotee Leaderboard</div>
              <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 28, fontSize: 16, lineHeight: 1.6 }}>The most devoted worshippers, ranked by daily login streaks. Log in every day to build your streak and climb the ranks. Tiers reset monthly.</p>
              {activeLeaderboard.length === 0 && (
                <div className="card" style={{ textAlign: "center", padding: "40px 24px", marginBottom: 24 }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#ff6b8a", marginBottom: 8 }}>
                    No Devotees Yet
                  </div>
                  <p style={{ fontSize: 14, color: "rgba(245,230,232,.5)", lineHeight: 1.5 }}>
                    Be the first to claim a spot! Log in daily to build your streak and earn your place on the leaderboard.
                  </p>
                </div>
              )}

              {activeLeaderboard.length >= 3 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 32, alignItems: "flex-end", flexWrap: "wrap" }}>
                {[activeLeaderboard[1], activeLeaderboard[0], activeLeaderboard[2]].map((entry, i) => {
                  const heights = [140, 180, 120];
                  const sizes = [60, 80, 60];
                  return (
                    <div key={entry.rank} style={{ textAlign: "center", animation: `fadeIn .6s ease ${i * 0.15}s both` }}>
                      <div style={{ fontSize: sizes[i] / 2, marginBottom: 8 }}>{entry.badge}</div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: i === 1 ? 18 : 15, fontWeight: 700, color: "#ff6b8a", marginBottom: 4 }}>{entry.name}</div>
                      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(245,230,232,.4)", marginBottom: 8 }}>#{entry.rank} · {entry.tier}</div>
                      <div style={{ width: sizes[i] + 20, height: heights[i], margin: "0 auto", background: "linear-gradient(180deg, rgba(220,20,60,.3), rgba(220,20,60,.05))", borderRadius: "8px 8px 0 0", border: "1px solid rgba(220,20,60,.2)", borderBottom: "none", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 16, fontSize: 24, fontWeight: 900, color: "#DC143C", fontFamily: "'Playfair Display', serif" }}>
                        #{entry.rank}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {activeLeaderboard.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {activeLeaderboard.map((entry, i) => (
                  <div key={entry.rank} className="expense-row" style={{ animation: `fadeIn .5s ease ${i * 0.05}s both`, background: entry.rank <= 3 ? "rgba(220,20,60,.06)" : "transparent" }}>
                    <div className="rank-badge" style={{ background: tierColors[entry.tier], fontFamily: "'Playfair Display', serif" }}>{entry.rank}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: "#f5e6e8", display: "flex", alignItems: "center", gap: 8 }}>{entry.badge} {entry.name}</div>
                      <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginTop: 2 }}>{entry.tier} Tier · {entry.streak} day streak</div>
                    </div>
                    <div style={{ display: "flex", gap: 2 }}>
                      {Array.from({ length: Math.min(Math.floor(entry.streak / 5), 5) }).map((_, j) => (
                        <div key={j} style={{ width: 8, height: 24 + j * 4, background: "linear-gradient(180deg, #DC143C, #8B0000)", borderRadius: 4, opacity: 0.5 + j * 0.1 }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              )}

              {/* Tier Legend */}
              <div style={{ marginTop: 24, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                {[
                  { tier: "Gold", badge: "🥇", desc: "Rank 1-3" },
                  { tier: "Silver", badge: "🥈", desc: "Rank 4-7" },
                  { tier: "Bronze", badge: "🥉", desc: "Rank 8+" },
                ].map((t, i) => (
                  <div key={i} style={{
                    padding: "10px 18px", borderRadius: 10,
                    background: "rgba(220,20,60,.05)", border: "1px solid rgba(220,20,60,.1)",
                    display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(245,230,232,.5)"
                  }}>
                    <span style={{ fontSize: 18 }}>{t.badge}</span>
                    <span style={{ fontWeight: 600, color: "#f5e6e8" }}>{t.tier}</span>
                    <span>· {t.desc}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, padding: 20, textAlign: "center", background: "rgba(220,20,60,.05)", borderRadius: 12, border: "1px solid rgba(220,20,60,.1)" }}>
                <div style={{ fontSize: 13, color: "rgba(245,230,232,.4)", fontStyle: "italic" }}>Rankings based on daily login streaks. Tiers reset on the 1st of each month. Log in every day to climb higher!</div>
              </div>
            </div>
          )}

          {/* ═══════ CHAT ═══════ */}
          {activeSection === "chat" && (
            <ChatRoom userRole={userRole} clerkUser={clerkUser} chatUsers={chatUsers} />
          )}

          {/* ═══════ MOD PANEL ═══════ */}
          {activeSection === "modpanel" && (userRole === "moderator" || userRole === "admin") && (
            <ModeratorPanel
              pendingConfessions={pendingConfessions}
              approveConfession={approveConfession}
              denyConfession={denyConfession}
              deleteConfession={deleteConfession}
              clerkUser={clerkUser}
            />
          )}

          {/* ═══════ ADMIN PANEL ═══════ */}
          {activeSection === "admin" && userRole === "admin" && (
            <AdminPanel
              clerkUser={clerkUser}
              pendingConfessions={pendingConfessions}
              approveConfession={approveConfession}
              denyConfession={denyConfession}
              deleteConfession={deleteConfession}
            />
          )}

          {/* ═══════ THRONE (Styled link-out, no iframe) ═══════ */}
          {activeSection === "throne" && (
            <div style={{ animation: "fadeIn .6s ease" }}>
              <div className="section-title">Throne Wishlist</div>
              <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 32, fontSize: 16, lineHeight: 1.6 }}>
                Send gifts and fulfill wishes from Goddess Selene's Throne wishlist.
              </p>

              {/* Throne Feature Card */}
              <div style={{
                maxWidth: 520, margin: "0 auto", borderRadius: 24, overflow: "hidden",
                border: "1px solid rgba(99,102,241,.3)",
                background: "linear-gradient(160deg, #0f0a1a, #1a0520, #0a0015)",
                boxShadow: "0 20px 60px rgba(99,102,241,.15), 0 0 80px rgba(236,72,153,.08)"
              }}>
                {/* Top gradient bar */}
                <div style={{
                  height: 4,
                  background: "linear-gradient(90deg, #00c9ff, #6366f1, #c026d3, #ec4899, #f97316)"
                }} />

                <div style={{ padding: "48px 36px", textAlign: "center" }}>
                  {/* Throne Logo (actual brand asset) */}
                  <div style={{ marginBottom: 28 }}>
                    <ThroneLogo width={280} />
                  </div>

                  <div style={{
                    width: 60, height: 2, margin: "0 auto 20px",
                    background: "linear-gradient(90deg, #6366f1, #ec4899)"
                  }} />

                  <p style={{
                    fontSize: 17, color: "rgba(245,230,232,.6)", lineHeight: 1.7,
                    maxWidth: 380, margin: "0 auto 32px"
                  }}>
                    Browse my curated wishlist and show your devotion through gifts. Every purchase is a tribute to perfection.
                  </p>

                  <a href={THRONE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                    <button style={{
                      padding: "16px 52px", border: "none", borderRadius: 50, fontSize: 16,
                      fontWeight: 700, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
                      letterSpacing: 2, textTransform: "uppercase", color: "#fff",
                      background: "linear-gradient(135deg, #6366f1, #c026d3, #ec4899)",
                      boxShadow: "0 4px 24px rgba(99,102,241,.4), 0 0 40px rgba(236,72,153,.15)",
                      transition: "all .3s"
                    }}>
                      Visit My Wishlist ↗
                    </button>
                  </a>

                  <div style={{
                    marginTop: 20, fontSize: 12, color: "rgba(245,230,232,.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6
                  }}>
                    <span>🔒</span> Secure gifting via Throne
                  </div>
                </div>

                {/* Bottom info strip */}
                <div style={{
                  padding: "16px 36px", background: "rgba(99,102,241,.06)",
                  borderTop: "1px solid rgba(99,102,241,.15)",
                  display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap"
                }}>
                  {[
                    { label: "Anonymous", icon: "🎭" },
                    { label: "Secure", icon: "🔐" },
                    { label: "Wishlist", icon: "✨" },
                  ].map((f, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      fontSize: 13, color: "rgba(245,230,232,.5)"
                    }}>
                      <span>{f.icon}</span> {f.label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: "rgba(245,230,232,.2)", fontStyle: "italic" }}>
                This links exclusively to Goddess Selene's Throne page.
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}


// ─── User Profile Modal ──────────────────────────────────────────
function UserProfileModal({ profile, clerkUser, userRole, onClose, onUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [nickname, setNickname] = useState(profile?.nickname || "");
  const [country, setCountry] = useState(profile?.country || "");
  const [age, setAge] = useState(profile?.age || "");
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isAdmin = userRole === "admin";
  const displayName = profile?.nickname || profile?.username || "Devotee";
  const roleColor = getRoleColor(profile?.role, profile?.username);

  const saveProfile = async () => {
    if (saving) return;
    setSaving(true);

    try {
      // Direct fields (no approval needed): nickname, country, age
      const updates = {};
      if (nickname.trim() && nickname !== profile?.nickname) updates.nickname = nickname.trim();
      if (country && country !== profile?.country) updates.country = country;
      if (age && parseInt(age) !== profile?.age) updates.age = parseInt(age);

      if (Object.keys(updates).length > 0) {
        if (isAdmin) {
          // Admin can update directly
          await supabase.from("user_profiles")
            .update(updates)
            .eq("clerk_user_id", clerkUser.id);
          if (onUpdate) onUpdate({ ...profile, ...updates });
        } else {
          // Regular users: update nickname/country/age directly (no approval needed)
          await supabase.from("user_profiles")
            .update(updates)
            .eq("clerk_user_id", clerkUser.id);
          if (onUpdate) onUpdate({ ...profile, ...updates });
        }
      }

      // Avatar needs admin approval (unless admin is changing their own)
      if (avatarFile) {
        const fileExt = avatarFile.name.split(".").pop();
        const fileName = `avatar-${clerkUser.id}-${Date.now()}.${fileExt}`;

        const { error: uploadErr } = await supabase.storage
          .from("content-images")
          .upload(fileName, avatarFile, { cacheControl: "3600", upsert: false });

        if (!uploadErr) {
          const { data: urlData } = supabase.storage
            .from("content-images")
            .getPublicUrl(fileName);

          if (isAdmin) {
            await supabase.from("user_profiles")
              .update({ avatar_url: urlData.publicUrl, avatar_approved: true })
              .eq("clerk_user_id", clerkUser.id);
            if (onUpdate) onUpdate({ ...profile, avatar_url: urlData.publicUrl, avatar_approved: true });
          } else {
            // Submit for approval
            await supabase.from("profile_change_requests").insert({
              user_id: clerkUser.id,
              username: profile?.username || "Devotee",
              field_name: "avatar_url",
              old_value: profile?.avatar_url || "",
              new_value: urlData.publicUrl,
              status: "pending",
            });
            setSubmitted(true);
          }
        }
      }

      if (!avatarFile || isAdmin) {
        setEditMode(false);
      }
    } catch (err) {
      console.error("Profile save error:", err);
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        maxWidth: 440, width: "100%", maxHeight: "90vh", overflowY: "auto",
        padding: "32px 28px", borderRadius: 20,
        background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
        border: "1px solid rgba(220,20,60,.3)", boxShadow: "0 20px 60px rgba(220,20,60,.2)",
      }} onClick={e => e.stopPropagation()}>

        {/* Avatar */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%", margin: "0 auto 12px",
            background: "linear-gradient(135deg, #DC143C, #8B0000)", padding: 3,
            boxShadow: `0 0 20px ${roleColor}40`,
          }}>
            <div style={{
              width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden",
              background: "#1a0505", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {profile?.avatar_url && profile?.avatar_approved ? (
                <img src={profile.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 32 }}>👤</span>
              )}
            </div>
          </div>

          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, color: roleColor }}>
            {getRoleBadge(profile?.role, profile?.username)} {displayName}
          </div>

          {profile?.role && profile.role !== "guest" && (
            <span style={{
              display: "inline-block", marginTop: 6, padding: "3px 12px", borderRadius: 20,
              fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
              background: profile.role === "admin" ? "rgba(96,165,250,.15)" : "rgba(255,215,0,.15)",
              color: profile.role === "admin" ? "#60a5fa" : "#FFD700",
              border: `1px solid ${profile.role === "admin" ? "rgba(96,165,250,.3)" : "rgba(255,215,0,.3)"}`,
            }}>{profile.role}</span>
          )}
        </div>

        {/* Profile Info */}
        {!editMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.04)" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>📝 Nickname</span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{profile?.nickname || "Not set"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.04)" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>🌍 Country</span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{profile?.country || "Not set"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.04)" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>🎂 Age</span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{profile?.age || "Not set"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.04)" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>📅 Joined</span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "Unknown"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(220,20,60,.04)" }}>
              <span style={{ fontSize: 13, color: "rgba(245,230,232,.5)" }}>🔥 Streak</span>
              <span style={{ fontSize: 13, color: "#f5e6e8", fontWeight: 600 }}>{profile?.current_streak || 0} days</span>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginBottom: 4, display: "block" }}>Nickname</label>
              <input value={nickname} onChange={e => setNickname(e.target.value)} maxLength={20} placeholder="Your display name"
                style={{ width: "100%", padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginBottom: 4, display: "block" }}>Country</label>
              <select value={country} onChange={e => setCountry(e.target.value)}
                style={{ width: "100%", padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif", cursor: "pointer" }}>
                <option value="">Select country...</option>
                {COUNTRIES.map((c, i) => <option key={i} value={c.n}>{c.f} {c.n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginBottom: 4, display: "block" }}>Age</label>
              <input type="number" value={age} onChange={e => setAge(e.target.value)} min={18} max={99} placeholder="18+"
                style={{ width: "100%", padding: "10px 14px", background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 10, color: "#f5e6e8", fontSize: 14, outline: "none", fontFamily: "'Cormorant Garamond', serif" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginBottom: 4, display: "block" }}>Profile Picture {!isAdmin && "(requires admin approval)"}</label>
              <div style={{ padding: "14px", borderRadius: 10, border: "1.5px dashed rgba(220,20,60,.3)", background: "rgba(220,20,60,.03)", textAlign: "center", position: "relative", overflow: "hidden", cursor: "pointer" }}>
                <input type="file" accept="image/*" onChange={e => setAvatarFile(e.target.files[0] || null)}
                  style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                {avatarFile ? (
                  <span style={{ color: "#ff6b8a", fontSize: 13 }}>✓ {avatarFile.name}</span>
                ) : (
                  <span style={{ color: "rgba(245,230,232,.4)", fontSize: 13 }}>📷 Click to upload</span>
                )}
              </div>
            </div>
            {submitted && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(76,175,80,.1)", border: "1px solid rgba(76,175,80,.2)", color: "#66bb6a", fontSize: 13, textAlign: "center" }}>
                ✓ Avatar submitted for admin approval
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          {editMode ? (
            <>
              <button onClick={() => setEditMode(false)} style={{
                flex: 1, padding: "10px", background: "transparent", border: "1px solid rgba(220,20,60,.2)",
                borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer",
                fontFamily: "'Cormorant Garamond', serif",
              }}>Cancel</button>
              <button onClick={saveProfile} disabled={saving} className="btn-primary" style={{
                flex: 1, padding: "10px", fontSize: 13, borderRadius: 10, opacity: saving ? 0.5 : 1,
              }}>{saving ? "Saving..." : "Save Changes"}</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditMode(true)} className="btn-primary" style={{
                flex: 1, padding: "10px", fontSize: 13, borderRadius: 10,
              }}>✏️ Edit Profile</button>
              <button onClick={onClose} style={{
                flex: 1, padding: "10px", background: "transparent", border: "1px solid rgba(220,20,60,.2)",
                borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer",
                fontFamily: "'Cormorant Garamond', serif",
              }}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ChatRoom Component ──────────────────────────────────────────
function ChatRoom({ userRole, clerkUser, chatUsers }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const [modRequestTarget, setModRequestTarget] = useState(null);
  const [modReason, setModReason] = useState("");
  const [muteConfirm, setMuteConfirm] = useState(null);
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  const canModerate = userRole === "moderator" || userRole === "admin";
  const isAdmin = userRole === "admin";
  const myId = clerkUser?.id;
  const myUsername = clerkUser?.username || clerkUser?.firstName || "Devotee";
  const [userRoles, setUserRoles] = useState({});

  // Load user roles for coloring
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const { data } = await supabase.from("user_profiles").select("clerk_user_id, role, username").limit(200);
        if (data) {
          const map = {};
          data.forEach(u => { map[u.clerk_user_id] = { role: u.role, username: u.username }; });
          setUserRoles(map);
        }
      } catch(e) {}
    };
    loadRoles();
  }, []);

  // Load messages + subscribe to real-time
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("is_deleted", false)
        .order("created_at", { ascending: true })
        .limit(100);
      if (data) setMessages(data);
    };
    load();

    channelRef.current = supabase
      .channel("chat-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, (p) => {
        if (!p.new.is_deleted) setMessages(prev => [...prev, p.new]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chat_messages" }, (p) => {
        if (p.new.is_deleted) {
          setMessages(prev => prev.filter(m => m.id !== p.new.id));
        } else {
          setMessages(prev => prev.map(m => m.id === p.new.id ? p.new : m));
        }
      })
      .subscribe();

    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, []);

  // Check mute status
  const checkMute = useCallback(async () => {
    if (!myId) return;
    const { data } = await supabase
      .from("user_mutes")
      .select("muted_until")
      .eq("user_id", myId)
      .gt("muted_until", new Date().toISOString())
      .limit(1);
    setIsMuted(data && data.length > 0);
  }, [myId]);

  useEffect(() => {
    checkMute();
    // Allow parent to trigger mute refresh
    window.__chatMuteRefresh = checkMute;
    return () => { delete window.__chatMuteRefresh; };
  }, [checkMute]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || isMuted) return;
    const username = clerkUser.username || clerkUser.firstName || "Devotee";
    await supabase.from("chat_messages").insert({
      user_id: myId,
      username: username,
      text: newMessage.trim(),
    });
    setNewMessage("");
  };

  const deleteMsg = async (id) => {
    await supabase.from("chat_messages").update({ is_deleted: true }).eq("id", id);
  };

  const muteUser = async (userId, username, minutes) => {
    const until = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    await supabase.from("user_mutes").insert({
      user_id: userId, username, muted_by: myId, muted_until: until,
    });
    setMuteConfirm(null);
  };

  const submitModRequest = async () => {
    if (!modRequestTarget || !modReason.trim()) return;
    await supabase.from("mod_requests").insert({
      requester_id: myId,
      requester_name: clerkUser.username || clerkUser.firstName || "Mod",
      target_user_id: modRequestTarget.userId,
      target_username: modRequestTarget.username,
      action_type: modRequestTarget.action,
      reason: modReason.trim(),
      status: "pending",
    });
    setModRequestTarget(null);
    setModReason("");
  };

  const adminBan = async (userId, username) => {
    await supabase.from("user_bans").insert({
      user_id: userId, username, banned_by: myId, reason: "Banned by admin", is_active: true,
    });
  };

  return (
    <div style={{ animation: "fadeIn .6s ease" }}>
      <div className="section-title">Chat Room</div>
      <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 16, fontSize: 15 }}>
        Real-time devotee chat. Be respectful or face the consequences.
      </p>

      {/* Messages Container */}
      <div style={{
        height: 450, overflowY: "auto", borderRadius: 16, padding: "16px",
        background: "linear-gradient(135deg, rgba(26,5,5,.9), rgba(40,8,8,.9))",
        border: "1px solid rgba(220,20,60,.2)", marginBottom: 12,
        display: "flex", flexDirection: "column", gap: 6,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "rgba(245,230,232,.3)" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
            No messages yet. Be the first to speak.
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.user_id === myId;
          return (
            <div key={msg.id} style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              padding: "10px 14px", borderRadius: 12,
              background: isMe ? "rgba(220,20,60,.08)" : "rgba(255,255,255,.02)",
              border: isMe ? "1px solid rgba(220,20,60,.15)" : "1px solid transparent",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: isMe ? "linear-gradient(135deg, #DC143C, #8B0000)" : "rgba(220,20,60,.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, color: "#fff",
              }}>{msg.username.charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: getRoleColor((chatUsers[msg.user_id]||{}).role, msg.username), cursor: "pointer" }}>
                    {getRoleBadge((chatUsers[msg.user_id]||{}).role, msg.username)} {msg.username}
                  </span>
                  <span style={{ fontSize: 10, color: "rgba(245,230,232,.25)" }}>{timeAgo(new Date(msg.created_at).getTime())}</span>
                </div>
                <div style={{ fontSize: 14, color: "rgba(245,230,232,.7)", wordBreak: "break-word", lineHeight: 1.4 }}>
                  {parseMessageWithMentions(msg.text).map((part, pi) =>
                    part.type === "mention" ? (
                      <span key={pi} style={{
                        color: "#60a5fa", fontWeight: 700, backgroundColor: "rgba(96,165,250,.1)",
                        padding: "1px 4px", borderRadius: 4, cursor: "pointer",
                      }}>{part.content}</span>
                    ) : (
                      <span key={pi}>{part.content}</span>
                    )
                  )}
                </div>
              </div>
              {/* Mod/Admin Controls */}
              {canModerate && !isMe && (
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button onClick={() => deleteMsg(msg.id)} title="Delete" style={{
                    width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                    background: "rgba(220,20,60,.15)", color: "#DC143C", fontSize: 12,
                  }}>🗑</button>
                  <button onClick={() => setMuteConfirm({ userId: msg.user_id, username: msg.username })} title="Mute" style={{
                    width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                    background: "rgba(255,152,0,.15)", color: "#ffa726", fontSize: 12,
                  }}>🔇</button>
                  {!isAdmin && (
                    <button onClick={() => setModRequestTarget({ userId: msg.user_id, username: msg.username, action: "ban" })} title="Request Ban" style={{
                      width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                      background: "rgba(99,102,241,.15)", color: "#818cf8", fontSize: 12,
                    }}>⚠️</button>
                  )}
                  {isAdmin && (
                    <button onClick={() => { if(window.confirm(`Ban ${msg.username}?`)) adminBan(msg.user_id, msg.username); }} title="Ban User" style={{
                      width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                      background: "rgba(220,20,60,.3)", color: "#ff4466", fontSize: 12,
                    }}>⛔</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isMuted ? (
        <div style={{ padding: "14px 20px", borderRadius: 12, background: "rgba(255,152,0,.1)", border: "1px solid rgba(255,152,0,.2)", textAlign: "center", color: "#ffa726", fontSize: 14 }}>
          🔇 You are currently muted.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") sendMessage(); }}
            placeholder="Type a message..."
            maxLength={500}
            style={{
              flex: 1, padding: "14px 18px", background: "rgba(26,5,5,.8)",
              border: "1px solid rgba(220,20,60,.3)", borderRadius: 12,
              color: "#f5e6e8", fontFamily: "'Cormorant Garamond', serif", fontSize: 15, outline: "none",
            }}
          />
          <button onClick={sendMessage} className="btn-primary" style={{ padding: "14px 24px", fontSize: 13, borderRadius: 12 }}>Send</button>
        </div>
      )}

      {/* Mute Confirmation Modal */}
      {muteConfirm && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
          background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setMuteConfirm(null)}>
          <div style={{
            maxWidth: 380, width: "100%", padding: "28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(255,152,0,.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32 }}>🔇</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#ffa726", marginTop: 8 }}>
                Mute {muteConfirm.username}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[{m:5,l:"5 minutes"},{m:15,l:"15 minutes"},{m:30,l:"30 minutes"},{m:60,l:"1 hour"},{m:1440,l:"24 hours"}].map(opt => (
                <button key={opt.m} onClick={() => muteUser(muteConfirm.userId, muteConfirm.username, opt.m)} style={{
                  padding: "12px", borderRadius: 10, background: "rgba(255,152,0,.1)", border: "1px solid rgba(255,152,0,.2)",
                  color: "#ffa726", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
                }}>{opt.l}</button>
              ))}
            </div>
            <button onClick={() => setMuteConfirm(null)} style={{
              width: "100%", marginTop: 12, padding: "10px", background: "transparent",
              border: "1px solid rgba(220,20,60,.2)", borderRadius: 10, color: "rgba(245,230,232,.4)",
              fontSize: 13, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Mod Request Modal */}
      {modRequestTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
          background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setModRequestTarget(null)}>
          <div style={{
            maxWidth: 420, width: "100%", padding: "28px", borderRadius: 20,
            background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))",
            border: "1px solid rgba(99,102,241,.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32 }}>⚠️</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#818cf8", marginTop: 8 }}>
                Request {modRequestTarget.action === "ban" ? "Ban" : "Kick"} for {modRequestTarget.username}
              </div>
              <p style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 6 }}>This request will be sent to Goddess Selene for approval.</p>
            </div>
            <textarea
              value={modReason}
              onChange={e => setModReason(e.target.value)}
              placeholder="Reason for this action (required)..."
              style={{
                width: "100%", padding: "14px", minHeight: 100, background: "rgba(10,0,0,.5)",
                border: "1px solid rgba(99,102,241,.3)", borderRadius: 12,
                color: "#f5e6e8", fontFamily: "'Cormorant Garamond', serif", fontSize: 15,
                outline: "none", resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => setModRequestTarget(null)} style={{
                flex: 1, padding: "12px", background: "transparent", border: "1px solid rgba(220,20,60,.2)",
                borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer",
                fontFamily: "'Cormorant Garamond', serif",
              }}>Cancel</button>
              <button onClick={submitModRequest} disabled={!modReason.trim()} className="btn-primary" style={{
                flex: 1, padding: "12px", fontSize: 13, borderRadius: 10,
                opacity: modReason.trim() ? 1 : 0.4,
              }}>Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Moderator Panel Component ───────────────────────────────────
function ModeratorPanel({ pendingConfessions, approveConfession, denyConfession, deleteConfession, clerkUser }) {
  const [activeTab, setActiveTab] = useState("confessions");
  const [mutes, setMutes] = useState([]);
  const [modRequests, setModRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [muteTarget, setMuteTarget] = useState(null);
  const [banRequestTarget, setBanRequestTarget] = useState(null);
  const [banReason, setBanReason] = useState("");

  useEffect(() => {
    const load = async () => {
      const { data: muteData } = await supabase.from("user_mutes").select("*").gt("muted_until", new Date().toISOString()).order("created_at", { ascending: false });
      if (muteData) setMutes(muteData);
      const { data: reqData } = await supabase.from("mod_requests").select("*").order("created_at", { ascending: false }).limit(50);
      if (reqData) setModRequests(reqData.filter(r => r.requester_id === clerkUser?.id));
      const { data: userData } = await supabase.from("user_profiles").select("*").neq("role", "admin").order("username", { ascending: true }).limit(100);
      if (userData) setUsers(userData);
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [clerkUser]);

  const muteUser = async (userId, username, minutes) => {
    const until = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    await supabase.from("user_mutes").insert({ user_id: userId, username, muted_by: clerkUser?.id, muted_until: until });
    setMutes(prev => [...prev, { user_id: userId, username, muted_until: until, id: Date.now() }]);
    setMuteTarget(null);
  };

  const submitBanRequest = async () => {
    if (!banRequestTarget || !banReason.trim()) return;
    await supabase.from("mod_requests").insert({
      requester_id: clerkUser?.id,
      requester_name: clerkUser?.username || clerkUser?.firstName || "Mod",
      target_user_id: banRequestTarget.clerk_user_id,
      target_username: banRequestTarget.username,
      action_type: "ban",
      reason: banReason.trim(),
      status: "pending",
    });
    setModRequests(prev => [{ target_username: banRequestTarget.username, action_type: "ban", reason: banReason, status: "pending", created_at: new Date().toISOString(), id: Date.now() }, ...prev]);
    setBanRequestTarget(null);
    setBanReason("");
  };

  const tabStyle = (t) => ({
    padding: "10px 20px", borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1, textTransform: "uppercase",
    background: activeTab === t ? "linear-gradient(135deg, #FFD700, #FFA500)" : "rgba(255,215,0,.08)",
    color: activeTab === t ? "#1a0505" : "rgba(245,230,232,.5)",
    border: activeTab === t ? "none" : "1.5px solid rgba(255,215,0,.2)", transition: "all .3s",
  });

  return (
    <div style={{ animation: "fadeIn .6s ease" }}>
      <div className="section-title">Moderator Panel</div>
      <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 20, fontSize: 15 }}>Review confessions, manage users, and keep the realm in order.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        <div style={tabStyle("confessions")} onClick={() => setActiveTab("confessions")}>
          🕯️ Confessions {pendingConfessions.length > 0 && <span style={{ background: "#FFD700", color: "#1a0505", borderRadius: 20, padding: "2px 8px", fontSize: 11, marginLeft: 6 }}>{pendingConfessions.length}</span>}
        </div>
        <div style={tabStyle("mutes")} onClick={() => setActiveTab("mutes")}>🔇 Mutes ({mutes.length})</div>
        <div style={tabStyle("requests")} onClick={() => setActiveTab("requests")}>⚠️ My Requests ({modRequests.length})</div>
        <div style={tabStyle("users")} onClick={() => setActiveTab("users")}>👥 Users</div>
      </div>

      {/* ── Pending Confessions ── */}
      {activeTab === "confessions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pendingConfessions.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No pending confessions</div>}
          {pendingConfessions.map(c => (
            <div key={c.id} className="card" style={{ padding: "18px 20px" }}>
              <div style={{ fontSize: 15, color: "rgba(245,230,232,.7)", fontStyle: "italic", lineHeight: 1.5, marginBottom: 10 }}>"{c.text}"</div>
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                <span>{c.author === "Anonymous" ? "🎭" : "👤"}</span>
                <span style={{ color: c.author === "Anonymous" ? "rgba(245,230,232,.4)" : "#ff6b8a" }}>{c.author}</span>
                <span style={{ color: "rgba(245,230,232,.2)" }}>·</span>
                {timeAgo(c.timestamp)}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => approveConfession(c.id)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif" }}>✓ Approve</button>
                <button onClick={() => denyConfession(c.id)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", background: "rgba(255,152,0,.15)", color: "#ffa726", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", border: "1px solid rgba(255,152,0,.3)" }}>✕ Deny</button>
                <button onClick={() => deleteConfession(c.id)} style={{ padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: "rgba(220,20,60,.15)", color: "#DC143C", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif" }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Active Mutes ── */}
      {activeTab === "mutes" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {mutes.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No active mutes</div>}
          {mutes.map(mute => (
            <div key={mute.id} className="expense-row">
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,152,0,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔇</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8" }}>{mute.username}</div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)" }}>Until {new Date(mute.muted_until).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── My Ban Requests ── */}
      {activeTab === "requests" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {modRequests.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No requests submitted</div>}
          {modRequests.map(req => (
            <div key={req.id} className="card" style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: "#ff6b8a", fontSize: 14 }}>⛔ BAN: {req.target_username}</span>
                <span style={{
                  padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                  background: req.status === "pending" ? "rgba(255,152,0,.15)" : req.status === "approved" ? "rgba(76,175,80,.15)" : "rgba(220,20,60,.15)",
                  color: req.status === "pending" ? "#ffa726" : req.status === "approved" ? "#66bb6a" : "#DC143C",
                }}>{req.status}</span>
              </div>
              <div style={{ fontSize: 13, color: "rgba(245,230,232,.5)", fontStyle: "italic" }}>"{req.reason}"</div>
              <div style={{ fontSize: 11, color: "rgba(245,230,232,.25)", marginTop: 4 }}>{timeAgo(new Date(req.created_at).getTime())}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Users (Mute + Request Ban) ── */}
      {activeTab === "users" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {users.map(u => (
            <div key={u.id} className="expense-row">
              <div style={{
                width: 36, height: 36, borderRadius: "50%", background: "rgba(220,20,60,.1)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#fff", fontWeight: 700,
              }}>{(u.nickname || u.username || "D").charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8" }}>{u.nickname || u.username}</div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)" }}>Streak: {u.current_streak}d</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setMuteTarget({ userId: u.clerk_user_id, username: u.nickname || u.username })} style={{
                  padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "rgba(255,152,0,.15)", color: "#ffa726", fontSize: 12, fontWeight: 700,
                  fontFamily: "'Cormorant Garamond', serif",
                }}>🔇 Mute</button>
                <button onClick={() => setBanRequestTarget(u)} style={{
                  padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "rgba(220,20,60,.15)", color: "#DC143C", fontSize: 12, fontWeight: 700,
                  fontFamily: "'Cormorant Garamond', serif",
                }}>⚠️ Request Ban</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mute Duration Picker Modal */}
      {muteTarget && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setMuteTarget(null)}>
          <div style={{ maxWidth: 380, width: "100%", padding: "28px", borderRadius: 20, background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))", border: "1px solid rgba(255,152,0,.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32 }}>🔇</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#ffa726", marginTop: 8 }}>Mute {muteTarget.username}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[{m:5,l:"5 minutes"},{m:15,l:"15 minutes"},{m:30,l:"30 minutes"},{m:60,l:"1 hour"},{m:1440,l:"24 hours"}].map(opt => (
                <button key={opt.m} onClick={() => muteUser(muteTarget.userId, muteTarget.username, opt.m)} style={{
                  padding: "12px", borderRadius: 10, background: "rgba(255,152,0,.1)", border: "1px solid rgba(255,152,0,.2)",
                  color: "#ffa726", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif",
                }}>{opt.l}</button>
              ))}
            </div>
            <button onClick={() => setMuteTarget(null)} style={{ width: "100%", marginTop: 12, padding: "10px", background: "transparent", border: "1px solid rgba(220,20,60,.2)", borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Ban Request Modal */}
      {banRequestTarget && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setBanRequestTarget(null)}>
          <div style={{ maxWidth: 420, width: "100%", padding: "28px", borderRadius: 20, background: "linear-gradient(135deg, rgba(26,5,5,.98), rgba(40,8,8,.98))", border: "1px solid rgba(220,20,60,.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32 }}>⚠️</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#DC143C", marginTop: 8 }}>Request Ban: {banRequestTarget.nickname || banRequestTarget.username}</div>
              <p style={{ fontSize: 13, color: "rgba(245,230,232,.4)", marginTop: 6 }}>This will be sent to admin for approval.</p>
            </div>
            <textarea value={banReason} onChange={e => setBanReason(e.target.value)} placeholder="Reason for ban request (required)..."
              style={{ width: "100%", padding: "14px", minHeight: 100, background: "rgba(10,0,0,.5)", border: "1px solid rgba(220,20,60,.3)", borderRadius: 12, color: "#f5e6e8", fontFamily: "'Cormorant Garamond', serif", fontSize: 15, outline: "none", resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => setBanRequestTarget(null)} style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid rgba(220,20,60,.2)", borderRadius: 10, color: "rgba(245,230,232,.4)", fontSize: 13, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif" }}>Cancel</button>
              <button onClick={submitBanRequest} disabled={!banReason.trim()} className="btn-primary" style={{ flex: 1, padding: "12px", fontSize: 13, borderRadius: 10, opacity: banReason.trim() ? 1 : 0.4 }}>Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin Panel Component ───────────────────────────────────────
function AdminPanel({ clerkUser, pendingConfessions, approveConfession, denyConfession, deleteConfession }) {
  const [activeTab, setActiveTab] = useState("requests");
  const [modRequests, setModRequests] = useState([]);
  const [bans, setBans] = useState([]);
  const [mutes, setMutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [profileRequests, setProfileRequests] = useState([]);
  const [userTasks, setUserTasks] = useState([]);

  useEffect(() => {
    const load = async () => {
      const { data: reqs } = await supabase.from("mod_requests").select("*").order("created_at", { ascending: false }).limit(50);
      if (reqs) setModRequests(reqs);

      const { data: banData } = await supabase.from("user_bans").select("*").eq("is_active", true).order("created_at", { ascending: false });
      if (banData) setBans(banData);

      const { data: muteData } = await supabase.from("user_mutes").select("*").gt("muted_until", new Date().toISOString()).order("created_at", { ascending: false });
      if (muteData) setMutes(muteData);

      const { data: userData } = await supabase.from("user_profiles").select("*").order("created_at", { ascending: false }).limit(100);
      if (userData) setUsers(userData);

      const { data: profileReqs } = await supabase.from("profile_change_requests").select("*").order("created_at", { ascending: false }).limit(50);
      if (profileReqs) setProfileRequests(profileReqs);

      // Load all user tasks for current month
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const { data: taskData } = await supabase
        .from("accepted_tasks")
        .select("*")
        .gte("accepted_at", monthStart)
        .order("accepted_at", { ascending: false });
      if (taskData) setUserTasks(taskData);
    };
    load();

    // Auto-refresh every 15 seconds
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  const approveRequest = async (req) => {
    await supabase.from("mod_requests").update({ status: "approved", reviewed_by: clerkUser.id }).eq("id", req.id);
    if (req.action_type === "ban") {
      await supabase.from("user_bans").insert({
        user_id: req.target_user_id, username: req.target_username,
        banned_by: clerkUser.id, reason: req.reason, is_active: true,
      });
    }
    setModRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: "approved" } : r));
  };

  const denyRequest = async (id) => {
    await supabase.from("mod_requests").update({ status: "denied", reviewed_by: clerkUser.id }).eq("id", id);
    setModRequests(prev => prev.map(r => r.id === id ? { ...r, status: "denied" } : r));
  };

  const unbanUser = async (banId) => {
    await supabase.from("user_bans").update({ is_active: false }).eq("id", banId);
    setBans(prev => prev.filter(b => b.id !== banId));
  };

  const setRole = async (userId, newRole) => {
    await supabase.from("user_profiles").update({ role: newRole }).eq("clerk_user_id", userId);
    setUsers(prev => prev.map(u => u.clerk_user_id === userId ? { ...u, role: newRole } : u));
  };

  const adminCompleteTask = async (taskId, userId) => {
    await supabase.from("accepted_tasks")
      .update({ status: "completed" })
      .eq("user_id", userId)
      .eq("task_id", taskId);
    setUserTasks(prev => prev.map(t => 
      t.user_id === userId && t.task_id === taskId ? { ...t, status: "completed" } : t
    ));
  };

  // Group tasks by user, pull real username from user_profiles
  const tasksByUser = {};
  userTasks.forEach(t => {
    const profile = users.find(u => u.clerk_user_id === t.user_id);
    const realName = profile?.nickname || profile?.username || t.username || t.user_id;
    if (!tasksByUser[t.user_id]) tasksByUser[t.user_id] = { username: realName, tasks: [] };
    tasksByUser[t.user_id].tasks.push(t);
  });

  const tabStyle = (t) => ({
    padding: "10px 20px", borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1, textTransform: "uppercase",
    background: activeTab === t ? "linear-gradient(135deg, #DC143C, #8B0000)" : "rgba(220,20,60,.08)",
    color: activeTab === t ? "#fff" : "rgba(245,230,232,.5)",
    border: activeTab === t ? "none" : "1.5px solid rgba(220,20,60,.2)", transition: "all .3s",
  });

  const pendingCount = modRequests.filter(r => r.status === "pending").length;

  return (
    <div style={{ animation: "fadeIn .6s ease" }}>
      <div className="section-title">Admin Panel</div>
      <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 20, fontSize: 15 }}>Manage users, review mod requests, and control the realm.</p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        <div style={tabStyle("requests")} onClick={() => setActiveTab("requests")}>
          📋 Requests {pendingCount > 0 && <span style={{ background: "#DC143C", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 11, marginLeft: 6 }}>{pendingCount}</span>}
        </div>
        <div style={tabStyle("bans")} onClick={() => setActiveTab("bans")}>🚫 Bans ({bans.length})</div>
        <div style={tabStyle("mutes")} onClick={() => setActiveTab("mutes")}>🔇 Mutes ({mutes.length})</div>
        <div style={tabStyle("users")} onClick={() => setActiveTab("users")}>👥 Users ({users.length})</div>
        <div style={tabStyle("tasks")} onClick={() => setActiveTab("tasks")}>📋 Tasks ({userTasks.length})</div>
        <div style={tabStyle("profiles")} onClick={() => setActiveTab("profiles")}>🖼️ Avatars</div>
        <div style={tabStyle("confessions")} onClick={() => setActiveTab("confessions")}>
          🕯️ Confessions {pendingConfessions.length > 0 && <span style={{ background: "#DC143C", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 11, marginLeft: 6 }}>{pendingConfessions.length}</span>}
        </div>
      </div>

      {/* ── Mod Requests ── */}
      {activeTab === "requests" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {modRequests.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No mod requests</div>}
          {modRequests.map(req => (
            <div key={req.id} className="card" style={{ padding: "18px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>{req.action_type === "ban" ? "⛔" : "👢"}</span>
                  <span style={{ fontWeight: 700, color: "#ff6b8a", fontSize: 15 }}>{req.action_type.toUpperCase()}: {req.target_username}</span>
                </div>
                <span style={{
                  padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                  background: req.status === "pending" ? "rgba(255,152,0,.15)" : req.status === "approved" ? "rgba(76,175,80,.15)" : "rgba(220,20,60,.15)",
                  color: req.status === "pending" ? "#ffa726" : req.status === "approved" ? "#66bb6a" : "#DC143C",
                }}>{req.status}</span>
              </div>
              <div style={{ fontSize: 14, color: "rgba(245,230,232,.6)", marginBottom: 6, fontStyle: "italic" }}>"{req.reason}"</div>
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)" }}>By {req.requester_name} · {timeAgo(new Date(req.created_at).getTime())}</div>
              {req.status === "pending" && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => approveRequest(req)} style={{
                    flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff",
                    fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
                  }}>✓ Approve</button>
                  <button onClick={() => denyRequest(req.id)} style={{
                    flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg, #8B0000, #DC143C)", color: "#fff",
                    fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
                  }}>✕ Deny</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Active Bans ── */}
      {activeTab === "bans" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {bans.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No active bans</div>}
          {bans.map(ban => (
            <div key={ban.id} className="expense-row">
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(220,20,60,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🚫</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8" }}>{ban.username}</div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)" }}>{ban.reason} · {timeAgo(new Date(ban.created_at).getTime())}</div>
              </div>
              <button onClick={() => unbanUser(ban.id)} style={{
                padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(76,175,80,.3)",
                background: "rgba(76,175,80,.1)", color: "#66bb6a", fontSize: 12, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Cormorant Garamond', serif",
              }}>Unban</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Active Mutes ── */}
      {activeTab === "mutes" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {mutes.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No active mutes</div>}
          {mutes.map(mute => (
            <div key={mute.id} className="expense-row">
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,152,0,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔇</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8" }}>{mute.username}</div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)" }}>Until {new Date(mute.muted_until).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Profile Change Requests ── */}
      {activeTab === "profiles" && (
        <ProfileRequestsTab clerkUser={clerkUser} />
      )}

      {/* ── User Tasks (Admin View) ── */}
      {activeTab === "tasks" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {Object.keys(tasksByUser).length === 0 && (
            <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No tasks accepted this month</div>
          )}
          {Object.entries(tasksByUser).map(([userId, userData]) => (
            <div key={userId} className="card" style={{ padding: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid rgba(220,20,60,.1)" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", background: "rgba(220,20,60,.15)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#ff6b8a",
                }}>{userData.username.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8" }}>{userData.username}</div>
                  <div style={{ fontSize: 11, color: "rgba(245,230,232,.35)" }}>
                    {userData.tasks.filter(t => t.status === "completed").length} completed · {userData.tasks.filter(t => t.status === "accepted").length} in progress
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {userData.tasks.map(t => {
                  const taskInfo = TASKS.find(tk => tk.id === t.task_id);
                  if (!taskInfo) return null;
                  const isDone = t.status === "completed";
                  return (
                    <div key={t.id} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8,
                      background: isDone ? "rgba(76,175,80,.06)" : "rgba(255,152,0,.06)",
                      border: isDone ? "1px solid rgba(76,175,80,.15)" : "1px solid rgba(255,152,0,.15)",
                    }}>
                      <span style={{ fontSize: 18 }}>{taskInfo.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: isDone ? "#66bb6a" : "#ffa726" }}>{taskInfo.title}</div>
                        <div style={{ fontSize: 11, color: "rgba(245,230,232,.3)" }}>{timeAgo(new Date(t.accepted_at).getTime())}</div>
                      </div>
                      {isDone ? (
                        <span style={{ fontSize: 11, color: "#66bb6a", fontWeight: 700, letterSpacing: 1 }}>✅ DONE</span>
                      ) : (
                        <button onClick={() => adminCompleteTask(t.task_id, t.user_id)} style={{
                          padding: "5px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                          background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff",
                          fontSize: 11, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
                        }}>✓ Complete</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── User Management ── */}
      {activeTab === "users" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {users.map(u => (
            <div key={u.id} className="expense-row">
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: u.role === "admin" ? "linear-gradient(135deg, #DC143C, #8B0000)" : u.role === "moderator" ? "linear-gradient(135deg, #6366f1, #818cf8)" : "rgba(220,20,60,.1)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#fff", fontWeight: 700,
              }}>{u.username.charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#f5e6e8", display: "flex", alignItems: "center", gap: 6 }}>
                  {u.username}
                  <span style={{
                    fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                    background: u.role === "admin" ? "rgba(220,20,60,.2)" : u.role === "moderator" ? "rgba(255,215,0,.15)" : u.role === "vip" ? "rgba(192,38,211,.15)" : u.role === "guest" ? "rgba(255,255,255,.03)" : "rgba(255,107,138,.1)",
                    color: u.role === "admin" ? "#DC143C" : u.role === "moderator" ? "#FFD700" : u.role === "vip" ? "#c026d3" : u.role === "guest" ? "rgba(245,230,232,.3)" : "#ff6b8a",
                  }}>{{ admin: "ADMIN", moderator: "MOD", vip: "VIP", guest: "GUEST", user: "SUB" }[u.role] || u.role}</span>
                </div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)" }}>Streak: {u.current_streak}d · Joined {new Date(u.created_at).toLocaleDateString()}</div>
              </div>
              {u.role !== "admin" && (
                <select
                  value={u.role}
                  onChange={e => setRole(u.clerk_user_id, e.target.value)}
                  style={{
                    padding: "6px 10px", borderRadius: 8, background: "rgba(10,0,0,.5)",
                    border: "1px solid rgba(220,20,60,.3)", color: "#f5e6e8", fontSize: 12,
                    fontFamily: "'Cormorant Garamond', serif", cursor: "pointer", outline: "none",
                  }}
                >
                  <option value="user">Sub</option>
                  <option value="vip">VIP</option>
                  <option value="moderator">Moderator</option>
                  <option value="guest">Guest</option>
                </select>
              )}
            </div>
          ))}
        </div>
      )}
      {/* ── Confession Approval (Admin) ── */}
      {activeTab === "confessions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pendingConfessions.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No pending confessions</div>}
          {pendingConfessions.map(c => (
            <div key={c.id} className="card" style={{ padding: "18px 20px" }}>
              <div style={{ fontSize: 15, color: "rgba(245,230,232,.7)", fontStyle: "italic", lineHeight: 1.5, marginBottom: 10 }}>"{c.text}"</div>
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                <span>{c.author === "Anonymous" ? "🎭" : "👤"}</span>
                <span style={{ color: c.author === "Anonymous" ? "rgba(245,230,232,.4)" : "#ff6b8a" }}>{c.author}</span>
                <span style={{ color: "rgba(245,230,232,.2)" }}>·</span>
                {timeAgo(c.timestamp)}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => approveConfession(c.id)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif" }}>✓ Approve</button>
                <button onClick={() => denyConfession(c.id)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", background: "rgba(255,152,0,.15)", color: "#ffa726", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", border: "1px solid rgba(255,152,0,.3)" }}>✕ Deny</button>
                <button onClick={() => deleteConfession(c.id)} style={{ padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: "rgba(220,20,60,.15)", color: "#DC143C", fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif" }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Profile Requests Tab (Admin) ────────────────────────────────
function ProfileRequestsTab({ clerkUser }) {
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("profile_change_requests").select("*").order("created_at", { ascending: false }).limit(50);
      if (data) setRequests(data);
    };
    load();
  }, []);

  const approveProfileChange = async (req) => {
    // Apply the change
    const updateObj = {};
    updateObj[req.field_name] = req.requested_value;
    await supabase.from("user_profiles").update(updateObj).eq("clerk_user_id", req.user_id);
    await supabase.from("profile_change_requests").update({ status: "approved", reviewed_by: clerkUser.id }).eq("id", req.id);
    setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: "approved" } : r));
  };

  const denyProfileChange = async (id) => {
    await supabase.from("profile_change_requests").update({ status: "denied", reviewed_by: clerkUser.id }).eq("id", id);
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "denied" } : r));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {requests.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "rgba(245,230,232,.3)" }}>No profile change requests</div>}
      {requests.map(req => (
        <div key={req.id} className="card" style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: "#ff6b8a", fontSize: 15 }}>
              {req.username} wants to change {req.field_name.replace("_", " ")}
            </span>
            <span style={{
              padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
              background: req.status === "pending" ? "rgba(255,152,0,.15)" : req.status === "approved" ? "rgba(76,175,80,.15)" : "rgba(220,20,60,.15)",
              color: req.status === "pending" ? "#ffa726" : req.status === "approved" ? "#66bb6a" : "#DC143C",
            }}>{req.status}</span>
          </div>
          <div style={{ fontSize: 13, color: "rgba(245,230,232,.5)", marginBottom: 4 }}>
            Current: <span style={{ color: "#f5e6e8" }}>{req.current_value || "(empty)"}</span>
          </div>
          <div style={{ fontSize: 13, color: "rgba(245,230,232,.5)", marginBottom: 8 }}>
            Requested: <span style={{ color: "#ff6b8a", fontWeight: 600 }}>{req.requested_value}</span>
          </div>
          <div style={{ fontSize: 11, color: "rgba(245,230,232,.3)" }}>{timeAgo(new Date(req.created_at).getTime())}</div>
          {req.status === "pending" && (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => approveProfileChange(req)} style={{
                flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff",
                fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
              }}>✓ Approve</button>
              <button onClick={() => denyProfileChange(req.id)} style={{
                flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #8B0000, #DC143C)", color: "#fff",
                fontSize: 13, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
              }}>✕ Deny</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Games Section ───────────────────────────────────────────────
function GamesSection({ confessional, setConfessional, confessName, setConfessName, isAnonymous, setIsAnonymous, confessions, submitConfession, deleteConfession, confessionSubmitted, acceptedTasks, acceptTask, userRole, completeTask, clerkUserId }) {
  const [activeGame, setActiveGame] = useState("wheel");

  return (
    <div style={{ animation: "fadeIn .6s ease" }}>
      <div className="section-title">Games & Rituals</div>
      <p style={{ color: "rgba(245,230,232,.5)", marginBottom: 24, fontSize: 16, lineHeight: 1.6 }}>
        Test your devotion. Every game is an opportunity to serve.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          { id: "wheel", label: "🎡 Spin the Wheel" },
          { id: "confess", label: "🕯️ Confessionals" },
          { id: "tasks", label: "📋 Tasks for Subs" },
        ].map(tab => (
          <div key={tab.id} className="game-tab" style={{
            background: activeGame === tab.id ? "linear-gradient(135deg, #DC143C, #8B0000)" : "rgba(220,20,60,.08)",
            color: activeGame === tab.id ? "#fff" : "rgba(245,230,232,.6)",
            borderColor: activeGame === tab.id ? "transparent" : "rgba(220,20,60,.2)"
          }} onClick={() => setActiveGame(tab.id)}>
            {tab.label}
          </div>
        ))}
      </div>

      {/* Spin the Wheel */}
      {activeGame === "wheel" && (
        <div className="card" style={{ padding: 32 }}>
          <div style={{ textAlign: "center", marginBottom: 24, fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#ff6b8a" }}>
            Test Your Fate — {WHEEL_SEGMENTS.length} Ways to Serve
          </div>
          <SpinWheel />
          <p style={{ textAlign: "center", marginTop: 20, fontSize: 14, color: "rgba(245,230,232,.4)", fontStyle: "italic" }}>
            Every spin demands obedience. There are no free passes.
          </p>
        </div>
      )}

      {/* Confessionals (with anonymous toggle) */}
      {activeGame === "confess" && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#ff6b8a", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              🕯️ Confess Your Sins
            </div>

            {/* Anonymous Toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 16, padding: "12px 16px", borderRadius: 10,
              background: "rgba(220,20,60,.06)", border: "1px solid rgba(220,20,60,.12)"
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#f5e6e8" }}>
                  {isAnonymous ? "🎭 Posting Anonymously" : "👤 Posting with Name"}
                </div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.4)", marginTop: 2 }}>
                  {isAnonymous ? "Your identity is hidden" : "Your name will be shown"}
                </div>
              </div>
              <button
                className="toggle-track"
                style={{ background: isAnonymous ? "rgba(220,20,60,.5)" : "#2e7d32" }}
                onClick={() => setIsAnonymous(!isAnonymous)}
              >
                <div className="toggle-knob" style={{ left: isAnonymous ? 3 : 23 }} />
              </button>
            </div>

            {/* Name input (shown when NOT anonymous) */}
            {!isAnonymous && (
              <div style={{ marginBottom: 14, animation: "fadeIn .3s ease" }}>
                <input
                  type="text"
                  className="name-input"
                  value={confessName}
                  onChange={e => setConfessName(e.target.value)}
                  placeholder="Enter your name or username..."
                  maxLength={30}
                />
              </div>
            )}

            <textarea
              className="confess-input"
              value={confessional}
              onChange={e => setConfessional(e.target.value)}
              placeholder="Confess to Goddess Selene... What have you done? What are you craving?"
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)" }}>
                Posting as: <span style={{ color: "#ff6b8a", fontWeight: 600 }}>
                  {isAnonymous ? "Anonymous" : (confessName.trim() || "Anonymous")}
                </span>
              </div>
              <button className="btn-primary" onClick={submitConfession} style={{ padding: "10px 28px", fontSize: 13 }}>
                Submit Confession
              </button>
            </div>

            {/* Pending notice */}
            {confessionSubmitted && (
              <div style={{
                marginTop: 12, padding: "10px 16px", borderRadius: 10,
                background: "rgba(255,152,0,.1)", border: "1px solid rgba(255,152,0,.2)",
                color: "#ffa726", fontSize: 13, textAlign: "center", animation: "fadeIn .3s ease"
              }}>⏳ Confession submitted — awaiting approval from a moderator</div>
            )}
          </div>

          <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "rgba(245,230,232,.3)", marginBottom: 12 }}>Approved Confessions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {confessions.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: "rgba(245,230,232,.25)", fontSize: 14 }}>No confessions yet. Be the first to confess.</div>
            )}
            {confessions.map((c, i) => (
              <div key={c.id || i} className="card" style={{ padding: "16px 20px", animation: `fadeIn .5s ease ${i * 0.1}s both`, position: "relative" }}>
                <div style={{ fontSize: 15, color: "rgba(245,230,232,.7)", fontStyle: "italic", lineHeight: 1.5 }}>"{c.text}"</div>
                <div style={{ fontSize: 12, color: "rgba(245,230,232,.3)", marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>{c.author === "Anonymous" ? "🎭" : "👤"}</span>
                  <span style={{ color: c.author === "Anonymous" ? "rgba(245,230,232,.4)" : "#ff6b8a", fontWeight: c.author !== "Anonymous" ? 600 : 400 }}>
                    {c.author}
                  </span>
                  <span style={{ color: "rgba(245,230,232,.2)" }}>·</span>
                  {timeAgo(c.timestamp)}
                </div>
                {/* Delete button for mod/admin */}
                {(userRole === "admin" || userRole === "moderator") && c.id && (
                  <button onClick={() => deleteConfession(c.id)} style={{
                    position: "absolute", top: 10, right: 10, width: 26, height: 26,
                    borderRadius: 6, border: "none", cursor: "pointer",
                    background: "rgba(220,20,60,.15)", color: "#DC143C", fontSize: 12,
                  }} title="Delete">🗑</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks for Subs (31 tasks — accept + admin completion) */}
      {activeGame === "tasks" && (
        <div>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 14, color: "rgba(245,230,232,.4)" }}>
              {Object.values(acceptedTasks).filter(s => s === "completed").length} completed · {Object.values(acceptedTasks).filter(s => s === "accepted").length} in progress
            </div>
            <div style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: "rgba(220,20,60,.1)", color: "#ff6b8a", letterSpacing: 1
            }}>
              {TASKS.length} TASKS · RESETS MONTHLY
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {TASKS.map((task, i) => {
              const taskStatus = acceptedTasks[task.id]; // undefined, "accepted", or "completed"
              const isAccepted = taskStatus === "accepted";
              const isCompleted = taskStatus === "completed";
              const cardClass = isCompleted ? "card-accepted" : isAccepted ? "card" : "card";
              return (
                <div key={task.id} className={cardClass} style={{
                  animation: `fadeIn .4s ease ${i * 0.04}s both`, padding: 20,
                  border: isCompleted ? "1px solid rgba(76,175,80,.4)" : isAccepted ? "1px solid rgba(255,152,0,.3)" : undefined,
                  background: isCompleted ? "linear-gradient(135deg, rgba(10,30,10,.9), rgba(20,40,15,.9))" : isAccepted ? "linear-gradient(135deg, rgba(40,30,5,.9), rgba(50,35,8,.9))" : undefined,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontSize: 28 }}>{task.icon}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {isCompleted && <span style={{ fontSize: 16, animation: "checkPop .4s ease" }}>✅</span>}
                      {isAccepted && <span style={{ fontSize: 16 }}>⏳</span>}
                      <span style={{
                        padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                        background: task.difficulty === "Easy" ? "rgba(76,175,80,.15)" : task.difficulty === "Medium" ? "rgba(255,152,0,.15)" : "rgba(220,20,60,.15)",
                        color: task.difficulty === "Easy" ? "#66bb6a" : task.difficulty === "Medium" ? "#ffa726" : "#DC143C",
                        border: `1px solid ${task.difficulty === "Easy" ? "rgba(76,175,80,.3)" : task.difficulty === "Medium" ? "rgba(255,152,0,.3)" : "rgba(220,20,60,.3)"}`
                      }}>{task.difficulty}</span>
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700, color: isCompleted ? "#66bb6a" : isAccepted ? "#ffa726" : "#ff6b8a", marginBottom: 6 }}>{task.title}</div>
                  <p style={{ fontSize: 13, color: "rgba(245,230,232,.5)", lineHeight: 1.5, marginBottom: 14 }}>{task.desc}</p>
                  {isCompleted ? (
                    <div style={{
                      width: "100%", padding: "10px", textAlign: "center", borderRadius: 50,
                      background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff",
                      fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
                      fontFamily: "'Cormorant Garamond', serif",
                      boxShadow: "0 4px 16px rgba(46,125,50,.3)"
                    }}>✓ COMPLETED</div>
                  ) : isAccepted ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{
                        flex: 1, padding: "10px", textAlign: "center", borderRadius: 50,
                        background: "linear-gradient(135deg, rgba(255,152,0,.2), rgba(255,152,0,.1))",
                        border: "1px solid rgba(255,152,0,.3)", color: "#ffa726",
                        fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
                        fontFamily: "'Cormorant Garamond', serif",
                      }}>⏳ In Progress</div>
                      {userRole === "admin" && (
                        <button onClick={() => completeTask(task.id, clerkUserId)} style={{
                          padding: "10px 16px", borderRadius: 50, border: "none", cursor: "pointer",
                          background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#fff",
                          fontSize: 12, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif",
                          letterSpacing: 1, textTransform: "uppercase",
                        }}>✓ Complete</button>
                      )}
                    </div>
                  ) : (
                    <button className="btn-outline" style={{ width: "100%", padding: "10px", fontSize: 12 }} onClick={() => acceptTask(task.id)}>Accept Task</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
//  APP WRAPPER — ClerkProvider + Auth Gate
//  This wraps the entire app in Clerk authentication.
//  Users must sign in/sign up before accessing the site.
// ═══════════════════════════════════════════════════════════════════
export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <SignedOut>
        <AuthGate />
      </SignedOut>
      <SignedIn>
        <GoddessSelene />
      </SignedIn>
    </ClerkProvider>
  );
}
